/* GENERATED FILE — do not edit. Built from modules/wheel-visualizer/api/shopify-showroom/ via 'npm run build:showroom'. */

// api/shopify-showroom/showroom-preselect.ts
function pickInitialWheelId(wheels, search) {
  const requested = new URLSearchParams(search).get("wheel")?.trim();
  if (requested && wheels.some((wheel) => wheel.id === requested)) return requested;
  return wheels[0]?.id ?? null;
}

// placement/placement-store.ts
var PlacementStore = class {
  placements = [];
  selectedId = null;
  hoveredId = null;
  listeners = /* @__PURE__ */ new Set();
  /** Returns a snapshot of all current placements. */
  getPlacements() {
    return [...this.placements];
  }
  /** Returns the currently selected placement's id, if any. */
  getSelectedId() {
    return this.selectedId;
  }
  /** Returns the currently selected placement, if any. */
  getSelected() {
    return this.placements.find((placement) => placement.id === this.selectedId) ?? null;
  }
  /** Returns the currently hovered placement's id, if any. */
  getHoveredId() {
    return this.hoveredId;
  }
  /** Adds a new placement, generating its id, and selects it. Returns the created placement. */
  add(placement) {
    const created = { ...placement, id: crypto.randomUUID() };
    this.placements = [...this.placements, created];
    this.selectedId = created.id;
    this.emit();
    return created;
  }
  /** Replaces a placement's geometry (the result of a drag/resize, manual or otherwise). No-ops if the id doesn't exist. */
  updateGeometry(id, geometry) {
    const index = this.placements.findIndex((placement) => placement.id === id);
    if (index === -1) return;
    const next = [...this.placements];
    next[index] = { ...next[index], geometry };
    this.placements = next;
    this.emit();
  }
  /** Selects a placement by id, or clears selection when passed `null`. */
  select(id) {
    if (this.selectedId === id) return;
    this.selectedId = id;
    this.emit();
  }
  /** Sets the hovered placement by id, or clears hover when passed `null`. Purely transient UI state — see `PlacementCollectionState.hoveredId`. */
  setHovered(id) {
    if (this.hoveredId === id) return;
    this.hoveredId = id;
    this.emit();
  }
  /** Removes a placement. Clears selection/hover if either referenced it. */
  remove(id) {
    const nextPlacements = this.placements.filter((placement) => placement.id !== id);
    if (nextPlacements.length === this.placements.length) return;
    this.placements = nextPlacements;
    if (this.selectedId === id) this.selectedId = null;
    if (this.hoveredId === id) this.hoveredId = null;
    this.emit();
  }
  /** Removes every placement and clears selection/hover — e.g. when a new image replaces the current one. */
  clear() {
    if (this.placements.length === 0 && this.selectedId === null && this.hoveredId === null) return;
    this.placements = [];
    this.selectedId = null;
    this.hoveredId = null;
    this.emit();
  }
  /** Subscribes to any change (add/update/select/hover/remove/clear). Returns an unsubscribe function. */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  emit() {
    const state = {
      placements: this.getPlacements(),
      selectedId: this.selectedId,
      hoveredId: this.hoveredId
    };
    this.listeners.forEach((listener) => listener(state));
  }
};

// placement/starter-guides.ts
function createStarterGuideGeometry(contentSize, index, count) {
  const width = contentSize?.width ?? 1200;
  const height = contentSize?.height ?? 800;
  const guideFractionsByCount = {
    1: [0.72],
    2: [0.43, 0.78]
  };
  const xFractions = guideFractionsByCount[count] ?? guideFractionsByCount[2];
  const majorAxis = Math.min(width * 0.095, height * 0.18);
  const minorAxis = majorAxis * 0.92;
  return {
    cx: width * xFractions[index],
    cy: height * 0.62,
    majorAxis,
    minorAxis,
    angle: 0
  };
}

// utils/texture-loader.ts
function loadTextureFromUrl(url) {
  return new Promise((resolve, reject) => {
    const element = new Image();
    element.crossOrigin = "anonymous";
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error(`Failed to load wheel texture: ${url}`));
    element.src = url;
  });
}

// assets/asset-manager.ts
var AssetManager = class {
  /**
   * @param loadTexture Decodes an image URL into an element. Defaults to
   * the real browser loader (`loadTextureFromUrl`); tests inject a fake
   * one to exercise caching/error-recovery without a DOM.
   */
  constructor(loadTexture = loadTextureFromUrl) {
    this.loadTexture = loadTexture;
  }
  cache = /* @__PURE__ */ new Map();
  /**
   * Preloads a wheel design's default (first) finish. Resolves
   * immediately, without loading anything, if the design has no finishes.
   */
  preload(wheel) {
    const finish = wheel.finishes[0];
    if (!finish) return Promise.resolve();
    return this.resolveTexture(wheel, finish).then(() => void 0);
  }
  /**
   * Resolves the decoded texture for a given wheel design + finish,
   * loading and caching it if not already resolved. Callers should let
   * this promise's rejection surface as a graceful fallback (e.g. keep
   * showing placeholder geometry) — see docs/wheel-visualizer-architecture.md
   * §10's "auto-detection is an accelerator, never a gate" principle,
   * applied here to asset loading: a failed texture must not break
   * placement interaction.
   */
  resolveTexture(wheel, finish) {
    const cached = this.cache.get(finish.textureUrl);
    if (cached) return cached;
    const promise = this.loadTexture(finish.textureUrl).then(
      (element) => ({
        wheelId: wheel.id,
        finishId: finish.id,
        element,
        dimensions: { width: element.naturalWidth, height: element.naturalHeight }
      })
    );
    promise.catch(() => this.cache.delete(finish.textureUrl));
    this.cache.set(finish.textureUrl, promise);
    return promise;
  }
  /** Clears the texture cache. Subsequent `resolveTexture` calls re-load, even for previously-resolved textures. */
  clear() {
    this.cache.clear();
  }
};

// assets/wheel-library.ts
var WheelLibrary = class {
  constructor(wheels = [], assetManager = new AssetManager()) {
    this.assetManager = assetManager;
    this.wheels = wheels;
    this.selectedWheelId = wheels[0]?.id ?? null;
  }
  wheels;
  selectedWheelId;
  /** Per-wheel finish choice (wheel id → finish id). Only holds explicit `selectFinish` choices; wheels never touched by it fall back to their first finish. Stale entries (wheel/finish gone after `setWheels`) are ignored at read time rather than pruned. */
  finishSelections = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  textures = /* @__PURE__ */ new Map();
  getWheels() {
    return this.wheels;
  }
  /**
   * Replaces the catalog (e.g. once a `CatalogLoader` resolves). The
   * current selection is preserved if it still exists among the new
   * wheels; otherwise selection falls back to the new list's first wheel
   * (or `null` for an empty catalog) — the same default-to-first
   * convention the constructor already uses. Does not touch the texture
   * cache — a wheel removed from the catalog simply becomes unreachable
   * through `getSelectedTexture()`, nothing more.
   */
  setWheels(wheels) {
    this.wheels = wheels;
    if (!wheels.some((wheel) => wheel.id === this.selectedWheelId)) {
      this.selectedWheelId = wheels[0]?.id ?? null;
    }
    this.notify();
  }
  getSelectedWheelId() {
    return this.selectedWheelId;
  }
  /** The selected wheel's active finish id — its explicitly chosen finish if still valid, else its first finish, else null (no selection / no finishes). */
  getSelectedFinishId() {
    const wheel = this.getSelectedWheel();
    if (!wheel) return null;
    return this.activeFinish(wheel)?.id ?? null;
  }
  /** The currently-selected wheel's decoded active-finish texture, or null if not yet resolved (or nothing is selected). */
  getSelectedTexture() {
    const wheel = this.getSelectedWheel();
    if (!wheel) return null;
    const finish = this.activeFinish(wheel);
    if (!finish) return null;
    return this.textures.get(this.textureKey(wheel.id, finish.id)) ?? null;
  }
  /**
   * Selects a wheel by id. A no-op for an unknown id or the
   * already-selected id (no redundant notification). Notifies
   * subscribers immediately with the new selection — even before its
   * texture resolves, so UI (e.g. a highlighted thumbnail) can update
   * without waiting on a load — and again once/if a texture that wasn't
   * already cached finishes resolving.
   */
  select(wheelId) {
    const wheel = this.wheels.find((candidate) => candidate.id === wheelId);
    if (!wheel || wheel.id === this.selectedWheelId) return;
    this.selectedWheelId = wheelId;
    this.notify();
    this.resolveAndCache(wheel, this.activeFinish(wheel)).catch(() => void 0);
  }
  /**
   * Selects one of a wheel's finishes by id. A no-op for an unknown
   * wheel/finish or the wheel's already-active finish. Follows `select`'s
   * notification contract: subscribers are notified immediately, and
   * again once a not-yet-cached texture resolves. The choice is
   * remembered per wheel, so switching wheels and back keeps it.
   */
  selectFinish(wheelId, finishId) {
    const wheel = this.wheels.find((candidate) => candidate.id === wheelId);
    if (!wheel) return;
    const finish = wheel.finishes.find((candidate) => candidate.id === finishId);
    if (!finish || this.activeFinish(wheel)?.id === finishId) return;
    this.finishSelections.set(wheelId, finishId);
    this.notify();
    this.resolveAndCache(wheel, finish).catch(() => void 0);
  }
  /**
   * Preloads every wheel's default (first) finish so subsequent
   * `select()` calls hit the cache. Wheels are resolved independently —
   * one failing (bad URL, network error) never blocks the rest of the
   * library from loading. Non-default finishes load lazily on their
   * first `selectFinish`.
   */
  preloadAll() {
    return Promise.all(this.wheels.map((wheel) => this.resolveAndCache(wheel, this.activeFinish(wheel)).catch(() => void 0))).then(
      () => void 0
    );
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  getSelectedWheel() {
    if (!this.selectedWheelId) return null;
    return this.wheels.find((wheel) => wheel.id === this.selectedWheelId) ?? null;
  }
  /** A wheel's active finish: its remembered `selectFinish` choice when still present, else its first finish. */
  activeFinish(wheel) {
    const chosenId = this.finishSelections.get(wheel.id);
    if (chosenId) {
      const chosen = wheel.finishes.find((finish) => finish.id === chosenId);
      if (chosen) return chosen;
    }
    return wheel.finishes[0] ?? null;
  }
  textureKey(wheelId, finishId) {
    return `${wheelId}/${finishId}`;
  }
  resolveAndCache(wheel, finish) {
    if (!finish) return Promise.resolve();
    const key = this.textureKey(wheel.id, finish.id);
    if (this.textures.has(key)) return Promise.resolve();
    return this.assetManager.resolveTexture(wheel, finish).then((texture) => {
      this.textures.set(key, texture);
      this.notify();
    });
  }
  notify() {
    const state = {
      wheels: this.wheels,
      selectedWheelId: this.selectedWheelId,
      selectedFinishId: this.getSelectedFinishId()
    };
    for (const listener of this.listeners) listener(state);
  }
};

// utils/image-validation.ts
var DEFAULT_MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

// utils/image-loader.ts
async function loadImageFromUrl(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`The scene image could not be fetched (HTTP ${response.status}).`);
  }
  const blob = await response.blob();
  const filename = new URL(url, globalThis.location?.href).pathname.split("/").pop() || "scene-image";
  return loadImageFile(new File([blob], filename, { type: blob.type || "application/octet-stream" }));
}
function loadImageFile(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const element = new Image();
    element.onload = () => {
      resolve({
        id: crypto.randomUUID(),
        element,
        dimensions: { width: element.naturalWidth, height: element.naturalHeight },
        sourceFile: file,
        objectUrl
      });
    };
    element.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The image could not be decoded. The file may be corrupted."));
    };
    element.src = objectUrl;
  });
}

// utils/geometry.ts
var HANDLE_RADIUS_PX = 7;
var HANDLE_HIT_RADIUS_PX = 18;
var MIN_AXIS_LENGTH_PX = 12;
function rotateVector(v, angle) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: v.x * cos - v.y * sin, y: v.x * sin + v.y * cos };
}
function toLocalEllipseSpace(point, geometry) {
  const relative = { x: point.x - geometry.cx, y: point.y - geometry.cy };
  return rotateVector(relative, -geometry.angle);
}
function isPointInEllipse(point, geometry) {
  const rx = geometry.majorAxis / 2;
  const ry = geometry.minorAxis / 2;
  if (rx <= 0 || ry <= 0) return false;
  const local = toLocalEllipseSpace(point, geometry);
  return local.x * local.x / (rx * rx) + local.y * local.y / (ry * ry) <= 1;
}
function getMajorAxisHandlePosition(geometry) {
  const local = { x: geometry.majorAxis / 2, y: 0 };
  const rotated = rotateVector(local, geometry.angle);
  return { x: geometry.cx + rotated.x, y: geometry.cy + rotated.y };
}
function getMajorAxisHandlePositionNegative(geometry) {
  const local = { x: -geometry.majorAxis / 2, y: 0 };
  const rotated = rotateVector(local, geometry.angle);
  return { x: geometry.cx + rotated.x, y: geometry.cy + rotated.y };
}
function getMinorAxisHandlePosition(geometry) {
  const local = { x: 0, y: geometry.minorAxis / 2 };
  const rotated = rotateVector(local, geometry.angle);
  return { x: geometry.cx + rotated.x, y: geometry.cy + rotated.y };
}
function getMinorAxisHandlePositionNegative(geometry) {
  const local = { x: 0, y: -geometry.minorAxis / 2 };
  const rotated = rotateVector(local, geometry.angle);
  return { x: geometry.cx + rotated.x, y: geometry.cy + rotated.y };
}
function getCornerHandlePositions(geometry) {
  const rx = geometry.majorAxis / 2;
  const ry = geometry.minorAxis / 2;
  return [
    { x: rx, y: ry },
    { x: rx, y: -ry },
    { x: -rx, y: ry },
    { x: -rx, y: -ry }
  ].map((local) => {
    const rotated = rotateVector(local, geometry.angle);
    return { x: geometry.cx + rotated.x, y: geometry.cy + rotated.y };
  });
}
function moveBy(geometry, delta) {
  return { ...geometry, cx: geometry.cx + delta.x, cy: geometry.cy + delta.y };
}
function resizeMajorAxis(geometry, pointer) {
  const local = toLocalEllipseSpace(pointer, geometry);
  return { ...geometry, majorAxis: Math.max(MIN_AXIS_LENGTH_PX, Math.abs(local.x) * 2) };
}
function resizeMinorAxis(geometry, pointer) {
  const local = toLocalEllipseSpace(pointer, geometry);
  return { ...geometry, minorAxis: Math.max(MIN_AXIS_LENGTH_PX, Math.abs(local.y) * 2) };
}
function resizeByDelta(geometry, delta) {
  const majorAxis = Math.max(MIN_AXIS_LENGTH_PX, geometry.majorAxis + delta);
  const scale = geometry.majorAxis > 0 ? majorAxis / geometry.majorAxis : 1;
  return {
    ...geometry,
    majorAxis,
    minorAxis: Math.max(MIN_AXIS_LENGTH_PX, geometry.minorAxis * scale)
  };
}
function resizeCorner(geometry, pointer) {
  const local = toLocalEllipseSpace(pointer, geometry);
  return {
    ...geometry,
    majorAxis: Math.max(MIN_AXIS_LENGTH_PX, Math.abs(local.x) * 2),
    minorAxis: Math.max(MIN_AXIS_LENGTH_PX, Math.abs(local.y) * 2)
  };
}

// ui/placement-interaction.ts
var DEFAULT_MAJOR_AXIS_RATIO = 0.18;
var DEFAULT_MINOR_AXIS_RATIO = 0.75;
var ENHANCED_HANDLE_HIT_RADIUS_PX = 22;
var KEYBOARD_STEP_PX = 1;
var KEYBOARD_STEP_SHIFT_PX = 10;
var GROW_KEYS = /* @__PURE__ */ new Set(["]", "}", "=", "+"]);
var SHRINK_KEYS = /* @__PURE__ */ new Set(["[", "{", "-", "_"]);
var ARROW_DELTAS = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 }
};
var CURSOR_BY_MODE = {
  move: "grab",
  "resize-major": "ew-resize",
  "resize-minor": "ns-resize",
  "resize-corner": "nwse-resize"
};
var PlacementInteraction = class {
  container;
  canvas;
  store;
  dragSession = null;
  currentCursor = "";
  cornerHandlesEnabled;
  handleHitRadiusPx;
  keyboardAdjustEnabled;
  /**
   * `cornerHandlesEnabled` opts into hit-testing the four corner resize
   * handles (both axes at once — see `resizeCorner`) and a larger hit
   * radius on every handle, matching `PlacementLayer`'s own
   * `cornerHandlesEnabled` sizing. Defaults to false, reproducing this
   * class's original hit-testing exactly — a host that never sets it
   * (DHA Wheel Studio) is unaffected. Only the internal Admin Tool
   * currently opts in.
   *
   * `keyboardAdjustEnabled` opts into precision keyboard adjustment of
   * the selected placement: arrow keys move it, `[`/`]` (or `-`/`=`)
   * resize it centered on itself preserving aspect ratio, 1 content px
   * per press or 10 with Shift. Defaults to false — only the internal
   * Admin Tool currently opts in; Delete/Backspace removal works for
   * every host regardless, as it always has.
   */
  constructor(container, canvas, store, cornerHandlesEnabled = false, keyboardAdjustEnabled = false) {
    this.container = container;
    this.canvas = canvas;
    this.store = store;
    this.cornerHandlesEnabled = cornerHandlesEnabled;
    this.handleHitRadiusPx = cornerHandlesEnabled ? ENHANCED_HANDLE_HIT_RADIUS_PX : HANDLE_HIT_RADIUS_PX;
    this.keyboardAdjustEnabled = keyboardAdjustEnabled;
    this.attachListeners();
  }
  /**
   * Adds a new placement centered in the current view, with a size
   * proportional to the displayed content, and selects it. Returns the
   * created placement.
   */
  addPlacementInView(imageId) {
    const containerSize = this.canvas.getContainerSize();
    const center = this.canvas.screenToContent({
      x: containerSize.width / 2,
      y: containerSize.height / 2
    });
    const contentSize = this.canvas.getContentSize();
    const majorAxis = contentSize ? contentSize.width * DEFAULT_MAJOR_AXIS_RATIO : 120;
    const minorAxis = majorAxis * DEFAULT_MINOR_AXIS_RATIO;
    return this.store.add({
      imageId,
      geometry: { cx: center.x, cy: center.y, majorAxis, minorAxis, angle: 0 },
      source: "manual"
    });
  }
  /** Deletes the currently selected placement, if any. */
  deleteSelected() {
    const selected = this.store.getSelected();
    if (selected) this.store.remove(selected.id);
  }
  /** Detaches all listeners. */
  destroy() {
    this.detachListeners();
  }
  hitTest(point) {
    const selected = this.store.getSelected();
    if (selected) {
      if (this.cornerHandlesEnabled) {
        for (const corner of getCornerHandlePositions(selected.geometry)) {
          if (distance(point, corner) <= this.handleHitRadiusPx) {
            return { placement: selected, mode: "resize-corner" };
          }
        }
      }
      if (distance(point, getMajorAxisHandlePosition(selected.geometry)) <= this.handleHitRadiusPx || distance(point, getMajorAxisHandlePositionNegative(selected.geometry)) <= this.handleHitRadiusPx) {
        return { placement: selected, mode: "resize-major" };
      }
      if (distance(point, getMinorAxisHandlePosition(selected.geometry)) <= this.handleHitRadiusPx || distance(point, getMinorAxisHandlePositionNegative(selected.geometry)) <= this.handleHitRadiusPx) {
        return { placement: selected, mode: "resize-minor" };
      }
    }
    const placements = this.store.getPlacements();
    for (let i = placements.length - 1; i >= 0; i--) {
      if (isPointInEllipse(point, placements[i].geometry)) {
        return { placement: placements[i], mode: "move" };
      }
    }
    return null;
  }
  applyDrag(placement, mode, point, previousPoint) {
    switch (mode) {
      case "move":
        return moveBy(placement.geometry, { x: point.x - previousPoint.x, y: point.y - previousPoint.y });
      case "resize-major":
        return resizeMajorAxis(placement.geometry, point);
      case "resize-minor":
        return resizeMinorAxis(placement.geometry, point);
      case "resize-corner":
        return resizeCorner(placement.geometry, point);
    }
  }
  /** Updates hover state and cursor for the given content-space point. No-ops (cheaply) if nothing changed, to avoid re-rendering on every pixel of mouse movement. */
  updateHover(point) {
    const hit = this.hitTest(point);
    this.setCursor(hit ? CURSOR_BY_MODE[hit.mode] : "");
    this.store.setHovered(hit ? hit.placement.id : null);
  }
  setCursor(cursor) {
    if (cursor === this.currentCursor) return;
    this.currentCursor = cursor;
    this.container.style.cursor = cursor;
  }
  handlePointerDown = (event) => {
    if (event.button !== 0) return;
    const point = this.canvas.clientPointToContent({ x: event.clientX, y: event.clientY });
    const hit = this.hitTest(point);
    if (!hit) {
      this.store.select(null);
      return;
    }
    event.stopPropagation();
    this.container.setPointerCapture(event.pointerId);
    this.store.select(hit.placement.id);
    this.dragSession = { placementId: hit.placement.id, mode: hit.mode, lastContentPoint: point };
    this.setCursor(hit.mode === "move" ? "grabbing" : CURSOR_BY_MODE[hit.mode]);
  };
  handlePointerMove = (event) => {
    const point = this.canvas.clientPointToContent({ x: event.clientX, y: event.clientY });
    if (!this.dragSession) {
      this.updateHover(point);
      return;
    }
    event.stopPropagation();
    const placement = this.store.getPlacements().find((p) => p.id === this.dragSession.placementId);
    if (!placement) {
      this.dragSession = null;
      return;
    }
    const nextGeometry = this.applyDrag(placement, this.dragSession.mode, point, this.dragSession.lastContentPoint);
    this.store.updateGeometry(placement.id, nextGeometry);
    this.dragSession.lastContentPoint = point;
  };
  handlePointerEnd = (event) => {
    if (!this.dragSession) return;
    event.stopPropagation();
    this.dragSession = null;
    this.updateHover(this.canvas.clientPointToContent({ x: event.clientX, y: event.clientY }));
  };
  handlePointerLeave = () => {
    if (this.dragSession) return;
    this.setCursor("");
    this.store.setHovered(null);
  };
  handleKeyDown = (event) => {
    if (isTextEntryTarget(event.target)) return;
    if (event.key === "Delete" || event.key === "Backspace") {
      if (!this.store.getSelected()) return;
      event.preventDefault();
      this.deleteSelected();
      return;
    }
    if (!this.keyboardAdjustEnabled) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const selected = this.store.getSelected();
    if (!selected) return;
    const step = event.shiftKey ? KEYBOARD_STEP_SHIFT_PX : KEYBOARD_STEP_PX;
    let nextGeometry = null;
    const arrowDelta = ARROW_DELTAS[event.key];
    if (arrowDelta) {
      nextGeometry = moveBy(selected.geometry, { x: arrowDelta.x * step, y: arrowDelta.y * step });
    } else if (GROW_KEYS.has(event.key)) {
      nextGeometry = resizeByDelta(selected.geometry, step);
    } else if (SHRINK_KEYS.has(event.key)) {
      nextGeometry = resizeByDelta(selected.geometry, -step);
    }
    if (!nextGeometry) return;
    event.preventDefault();
    this.store.updateGeometry(selected.id, nextGeometry);
  };
  attachListeners() {
    this.container.addEventListener("pointerdown", this.handlePointerDown, { capture: true });
    this.container.addEventListener("pointermove", this.handlePointerMove, { capture: true });
    this.container.addEventListener("pointerup", this.handlePointerEnd, { capture: true });
    this.container.addEventListener("pointercancel", this.handlePointerEnd, { capture: true });
    this.container.addEventListener("pointerleave", this.handlePointerLeave, { capture: true });
    document.addEventListener("keydown", this.handleKeyDown);
  }
  detachListeners() {
    this.container.removeEventListener("pointerdown", this.handlePointerDown, { capture: true });
    this.container.removeEventListener("pointermove", this.handlePointerMove, { capture: true });
    this.container.removeEventListener("pointerup", this.handlePointerEnd, { capture: true });
    this.container.removeEventListener("pointercancel", this.handlePointerEnd, { capture: true });
    this.container.removeEventListener("pointerleave", this.handlePointerLeave, { capture: true });
    document.removeEventListener("keydown", this.handleKeyDown);
  }
};
function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
function isTextEntryTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

// utils/viewport-math.ts
function clampScale(scale, minScale, maxScale) {
  return Math.min(maxScale, Math.max(minScale, scale));
}
function computeFitTransform(contentSize, containerSize) {
  if (contentSize.width <= 0 || contentSize.height <= 0 || containerSize.width <= 0 || containerSize.height <= 0) {
    return { scale: 1, offset: { x: 0, y: 0 } };
  }
  const scale = Math.min(
    containerSize.width / contentSize.width,
    containerSize.height / contentSize.height
  );
  return {
    scale,
    offset: {
      x: (containerSize.width - contentSize.width * scale) / 2,
      y: (containerSize.height - contentSize.height * scale) / 2
    }
  };
}
function screenToContent(point, transform) {
  return {
    x: (point.x - transform.offset.x) / transform.scale,
    y: (point.y - transform.offset.y) / transform.scale
  };
}
function contentToScreen(point, transform) {
  return {
    x: point.x * transform.scale + transform.offset.x,
    y: point.y * transform.scale + transform.offset.y
  };
}
function zoomAtPoint(transform, nextScale, focalPoint) {
  const contentPoint = screenToContent(focalPoint, transform);
  return {
    scale: nextScale,
    offset: {
      x: focalPoint.x - contentPoint.x * nextScale,
      y: focalPoint.y - contentPoint.y * nextScale
    }
  };
}
function panTransform(transform, delta) {
  return {
    scale: transform.scale,
    offset: {
      x: transform.offset.x + delta.x,
      y: transform.offset.y + delta.y
    }
  };
}
function constrainTransformToContainer(transform, contentSize, containerSize) {
  const constrainAxis = (offset, scaledContent, container) => scaledContent <= container ? (container - scaledContent) / 2 : Math.min(0, Math.max(container - scaledContent, offset));
  return {
    scale: transform.scale,
    offset: {
      x: constrainAxis(transform.offset.x, contentSize.width * transform.scale, containerSize.width),
      y: constrainAxis(transform.offset.y, contentSize.height * transform.scale, containerSize.height)
    }
  };
}
function computeClampedFitTransform(contentSize, containerSize, minScale, maxScale) {
  const fitTransform = computeFitTransform(contentSize, containerSize);
  const clampedScale = clampScale(fitTransform.scale, minScale, maxScale);
  if (clampedScale === fitTransform.scale) return fitTransform;
  return zoomAtPoint(fitTransform, clampedScale, {
    x: containerSize.width / 2,
    y: containerSize.height / 2
  });
}

// utils/pixel-source.ts
var MAX_SAMPLE_DIMENSION = 1e3;
function createPixelSource(image, contentWidth, contentHeight) {
  const scale = Math.min(1, MAX_SAMPLE_DIMENSION / Math.max(contentWidth, contentHeight));
  const width = Math.max(1, Math.round(contentWidth * scale));
  const height = Math.max(1, Math.round(contentHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    throw new Error("createPixelSource requires 2D canvas support.");
  }
  ctx.drawImage(image, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  return { width, height, data: imageData.data, scale };
}

// utils/tone-match.ts
var DEFAULT_INNER_SCALE = 1.05;
var DEFAULT_OUTER_SCALE = 1.6;
var DEFAULT_RING_STEPS = 4;
var DEFAULT_SAMPLES_PER_RING = 32;
var SHADOW_PERCENTILE = 0.15;
var HIGHLIGHT_PERCENTILE = 0.85;
var REFERENCE_LUMINANCE = 150;
var REFERENCE_SPREAD = 110;
var BRIGHTNESS_SENSITIVITY = 0.6;
var CONTRAST_SENSITIVITY = 0.5;
var BRIGHTNESS_MULTIPLIER_RANGE = { min: 0.75, max: 1.2 };
var CONTRAST_MULTIPLIER_RANGE = { min: 0.8, max: 1.25 };
var EFFECTIVE_BRIGHTNESS_RANGE = { min: 0.55, max: 1.35 };
var EFFECTIVE_CONTRAST_RANGE = { min: 0.7, max: 1.45 };
function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}
function luminance(r, g, b) {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function sampleSceneToneStats(pixels, cx, cy, rx, ry, angle, innerScale = DEFAULT_INNER_SCALE, outerScale = DEFAULT_OUTER_SCALE, ringSteps = DEFAULT_RING_STEPS, samplesPerRing = DEFAULT_SAMPLES_PER_RING) {
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);
  const samples = [];
  for (let ring = 0; ring < ringSteps; ring++) {
    const t = ringSteps === 1 ? 0 : ring / (ringSteps - 1);
    const scale = innerScale + (outerScale - innerScale) * t;
    for (let i = 0; i < samplesPerRing; i++) {
      const theta = i / samplesPerRing * Math.PI * 2;
      const localX = scale * rx * Math.cos(theta);
      const localY = scale * ry * Math.sin(theta);
      const x = Math.round(cx + localX * cosA - localY * sinA);
      const y = Math.round(cy + localX * sinA + localY * cosA);
      if (x < 0 || y < 0 || x >= pixels.width || y >= pixels.height) continue;
      const idx = (y * pixels.width + x) * 4;
      samples.push(luminance(pixels.data[idx], pixels.data[idx + 1], pixels.data[idx + 2]));
    }
  }
  if (samples.length === 0) return null;
  samples.sort((a, b) => a - b);
  const mean = samples.reduce((sum, value) => sum + value, 0) / samples.length;
  const shadowIndex = Math.min(samples.length - 1, Math.floor(samples.length * SHADOW_PERCENTILE));
  const highlightIndex = Math.min(samples.length - 1, Math.floor(samples.length * HIGHLIGHT_PERCENTILE));
  return {
    meanLuminance: mean,
    shadowLevel: samples[shadowIndex],
    highlightLevel: samples[highlightIndex],
    sampleCount: samples.length
  };
}
function computeToneAdjustment(stats, strength) {
  const clampedStrength = clamp(strength, 0, 1);
  const lumaRatio = stats.meanLuminance / REFERENCE_LUMINANCE;
  const rawBrightness = 1 + (lumaRatio - 1) * BRIGHTNESS_SENSITIVITY;
  const brightnessMultiplier = clamp(
    1 + (rawBrightness - 1) * clampedStrength,
    BRIGHTNESS_MULTIPLIER_RANGE.min,
    BRIGHTNESS_MULTIPLIER_RANGE.max
  );
  const sceneSpread = Math.max(0, stats.highlightLevel - stats.shadowLevel);
  const spreadRatio = sceneSpread / REFERENCE_SPREAD;
  const rawContrast = 1 + (spreadRatio - 1) * CONTRAST_SENSITIVITY;
  const contrastMultiplier = clamp(
    1 + (rawContrast - 1) * clampedStrength,
    CONTRAST_MULTIPLIER_RANGE.min,
    CONTRAST_MULTIPLIER_RANGE.max
  );
  return { brightnessMultiplier, contrastMultiplier };
}
function resolveEffectiveWheelFilter(base, stats) {
  if (!base.autoToneMatchEnabled || base.autoToneStrength <= 0 || !stats) return base;
  const { brightnessMultiplier, contrastMultiplier } = computeToneAdjustment(stats, base.autoToneStrength);
  return {
    ...base,
    brightness: clamp(base.brightness * brightnessMultiplier, EFFECTIVE_BRIGHTNESS_RANGE.min, EFFECTIVE_BRIGHTNESS_RANGE.max),
    contrast: clamp(base.contrast * contrastMultiplier, EFFECTIVE_CONTRAST_RANGE.min, EFFECTIVE_CONTRAST_RANGE.max)
  };
}

// viewport/preview-canvas.ts
var DEFAULT_MIN_SCALE = 0.1;
var DEFAULT_MAX_SCALE = 8;
var WHEEL_ZOOM_SENSITIVITY = 15e-4;
var TAP_MAX_TRAVEL_PX = 8;
var DOUBLE_TAP_MAX_DELAY_MS = 350;
var DOUBLE_TAP_MAX_DISTANCE_PX = 40;
var PreviewCanvas = class {
  container;
  stage;
  minScale;
  maxScale;
  interactionEnabled;
  /** Multiples of the current fit scale that override minScale/maxScale — see setRelativeScaleBounds. */
  relativeScaleBounds = null;
  /** When true, every transform is passed through constrainTransformToContainer — see setConstrainToContainer. */
  constrainToContainer = false;
  layers = /* @__PURE__ */ new Map();
  viewportListeners = /* @__PURE__ */ new Set();
  resizeListeners = /* @__PURE__ */ new Set();
  doubleActivateListeners = /* @__PURE__ */ new Set();
  resizeObserver = null;
  containerSize = { width: 0, height: 0 };
  contentSize = null;
  transform = { scale: 1, offset: { x: 0, y: 0 } };
  /** True once the user has manually zoomed or panned — suppresses auto-refit on resize/content change. */
  hasCustomView = false;
  activePointers = /* @__PURE__ */ new Map();
  pinchStartDistance = 0;
  pinchStartScale = 1;
  /** Down positions per pointer, for tap (vs drag) detection. */
  pointerDownPoints = /* @__PURE__ */ new Map();
  /** Set the moment a second pointer joins — the whole gesture stops counting as a tap. */
  gestureHadMultiplePointers = false;
  lastTapTime = 0;
  lastTapPoint = null;
  constructor(container, options = {}) {
    this.container = container;
    this.minScale = options.minScale ?? DEFAULT_MIN_SCALE;
    this.maxScale = options.maxScale ?? DEFAULT_MAX_SCALE;
    this.interactionEnabled = options.interactionEnabled ?? true;
    this.container.style.position = "relative";
    this.container.style.overflow = "hidden";
    this.container.style.touchAction = "none";
    this.container.style.userSelect = "none";
    if (!this.interactionEnabled) {
      this.container.style.cursor = "default";
    }
    this.container.addEventListener("dragstart", (event) => event.preventDefault());
    this.stage = document.createElement("div");
    this.stage.style.position = "absolute";
    this.stage.style.left = "0";
    this.stage.style.top = "0";
    this.stage.style.transformOrigin = "0 0";
    this.container.appendChild(this.stage);
    this.containerSize = { width: this.container.clientWidth, height: this.container.clientHeight };
    this.attachResizeObserver();
    if (this.interactionEnabled) {
      this.attachInteractionListeners();
    }
  }
  // ── Content & layers ──────────────────────────────────────────────────
  /**
   * Sets the intrinsic size of the content this viewport will display
   * (e.g. a newly uploaded or replacement image's natural dimensions).
   * Always triggers an automatic fit-to-container, resetting any prior
   * pan/zoom — call this only when a new/replacement image is being
   * displayed, not for incidental updates, since it unconditionally
   * discards the user's current view. (`displayImageInCanvas`, the sole
   * caller, only ever calls this from a freshly uploaded image, matching
   * that contract.) Auto-fit resuming on every new image — even after the
   * user has zoomed/panned a previous one — is intentional: `hasCustomView`
   * exists to protect the user's view against *incidental* changes
   * (a window resize, a wheel placement/finish change), not against a
   * deliberate new upload, which should always start the user looking at
   * their whole photo.
   */
  setContentSize(size) {
    this.contentSize = size;
    this.stage.style.width = `${size.width}px`;
    this.stage.style.height = `${size.height}px`;
    this.fitToContainer();
  }
  /** Registers a layer to be hosted in the viewport, in `zIndex` order. */
  addLayer(layer) {
    this.layers.set(layer.id, layer);
    layer.element.style.position = "absolute";
    layer.element.style.left = "0";
    layer.element.style.top = "0";
    layer.element.style.zIndex = String(layer.zIndex);
    this.stage.appendChild(layer.element);
    layer.onResize?.(this.containerSize);
    layer.onViewportChange?.(this.transform);
  }
  /** Removes a previously-registered layer. */
  removeLayer(id) {
    const layer = this.layers.get(id);
    if (!layer) return;
    this.stage.removeChild(layer.element);
    this.layers.delete(id);
  }
  /** Returns a previously-registered layer by id, if present. */
  getLayer(id) {
    return this.layers.get(id);
  }
  // ── Viewport transform ──────────────────────────────────────────────────
  /** Returns the current pan/zoom transform. */
  getTransform() {
    return this.transform;
  }
  /** Returns the current container size, in CSS pixels. */
  getContainerSize() {
    return this.containerSize;
  }
  /** Returns the content size set via `setContentSize`, if any has been set yet. */
  getContentSize() {
    return this.contentSize;
  }
  /** Resets the view to fit the current content inside the container, centered. */
  fitToContainer() {
    this.hasCustomView = false;
    if (this.contentSize) {
      const { min, max } = this.effectiveScaleBounds();
      this.setTransform(computeClampedFitTransform(this.contentSize, this.containerSize, min, max));
    }
  }
  /**
   * The scale at which the current content exactly fits the container —
   * "100%" in fit-relative terms. Null before any content is set. Hosts
   * expressing zoom levels as multiples of the fitted view (an image
   * inspection UI) multiply this by their level before calling zoomTo.
   */
  getFitScale() {
    if (!this.contentSize) return null;
    return computeFitTransform(this.contentSize, this.containerSize).scale;
  }
  /**
   * Enables or disables user pan/zoom gestures at runtime — a host can
   * keep a preview static in its page layout and turn interactions on
   * only while a fullscreen inspection mode is open. Enabling also sets a
   * `grab` cursor on the container (disabling restores `default`, the
   * same cursor the constructor sets for a non-interactive canvas).
   */
  setInteractionEnabled(enabled) {
    if (enabled === this.interactionEnabled) return;
    this.interactionEnabled = enabled;
    if (enabled) {
      this.attachInteractionListeners();
      this.container.style.cursor = "grab";
    } else {
      this.detachInteractionListeners();
      this.container.style.cursor = "default";
      this.activePointers.clear();
      this.pointerDownPoints.clear();
      this.pinchStartDistance = 0;
      this.gestureHadMultiplePointers = false;
    }
  }
  /**
   * Reinterprets the zoom bounds as multiples of the current fit scale
   * (1 = the whole content exactly fits), recomputed dynamically as the
   * content or container changes — so "min zoom = fitted view, max = 5×"
   * holds for every image and viewport size. Pass null to restore the
   * constructor's absolute minScale/maxScale.
   */
  setRelativeScaleBounds(bounds) {
    this.relativeScaleBounds = bounds;
  }
  /**
   * When enabled, every pan/zoom result is constrained so the content
   * stays anchored to the container: no gap can open along an axis where
   * the content is larger, and the content stays centered along an axis
   * where it is smaller (see constrainTransformToContainer). This also
   * makes dragging a fully-fitted image a no-op.
   */
  setConstrainToContainer(enabled) {
    this.constrainToContainer = enabled;
    if (enabled && this.contentSize) this.setTransform(this.transform);
  }
  /**
   * Forces an immediate, synchronous re-measurement of the container's
   * current size (and re-fits any displayed content to it). The
   * `ResizeObserver` this class otherwise relies on only reports a
   * container's size asynchronously, on the browser's own schedule —
   * fine for ongoing window-resize handling, but too late for a host
   * that reveals a previously-hidden (`display:none`) container and
   * immediately displays content into it: the very first `setContentSize`
   * would fit against a stale zero-size container instead of the real
   * one. Call this right after making the container visible, before
   * mounting content.
   */
  remeasureContainer() {
    this.containerSize = { width: this.container.clientWidth, height: this.container.clientHeight };
    if (!this.hasCustomView && this.contentSize) {
      const { min, max } = this.effectiveScaleBounds();
      this.setTransform(computeClampedFitTransform(this.contentSize, this.containerSize, min, max));
    } else if (this.constrainToContainer && this.contentSize) {
      this.setTransform(this.transform);
    }
    this.layers.forEach((layer) => layer.onResize?.(this.containerSize));
  }
  /** Sets the absolute zoom scale, keeping `focalPoint` (default: container center) visually fixed. */
  zoomTo(scale, focalPoint) {
    if (!this.interactionEnabled) return;
    const { min, max } = this.effectiveScaleBounds();
    const nextScale = clampScale(scale, min, max);
    const focal = focalPoint ?? { x: this.containerSize.width / 2, y: this.containerSize.height / 2 };
    this.hasCustomView = true;
    this.setTransform(zoomAtPoint(this.transform, nextScale, focal));
  }
  /** Multiplies the current zoom scale by `factor`, keeping `focalPoint` (default: container center) visually fixed. */
  zoomBy(factor, focalPoint) {
    this.zoomTo(this.transform.scale * factor, focalPoint);
  }
  /** Pans the view by a screen-space delta. */
  panBy(delta) {
    if (!this.interactionEnabled) return;
    this.hasCustomView = true;
    this.setTransform(panTransform(this.transform, delta));
  }
  // ── Coordinate conversion ────────────────────────────────────────────────
  /** Converts a point in screen space (relative to the container) to content space. */
  screenToContent(point) {
    return screenToContent(point, this.transform);
  }
  /** Converts a point in content space to screen space (relative to the container). */
  contentToScreen(point) {
    return contentToScreen(point, this.transform);
  }
  /**
   * Converts a raw pointer/mouse event's client coordinates (`clientX`/
   * `clientY`, viewport-relative) directly to content-space. The
   * composition every interactive layer needs, since pointer events give
   * client coordinates, not the container-relative screen coordinates
   * `screenToContent` expects.
   */
  clientPointToContent(clientPoint) {
    const rect = this.container.getBoundingClientRect();
    return this.screenToContent({ x: clientPoint.x - rect.left, y: clientPoint.y - rect.top });
  }
  // ── Events ───────────────────────────────────────────────────────────────
  /** Subscribes to pan/zoom transform changes. Returns an unsubscribe function. */
  onViewportChange(listener) {
    this.viewportListeners.add(listener);
    return () => this.viewportListeners.delete(listener);
  }
  /** Subscribes to container resize events. Returns an unsubscribe function. */
  onResize(listener) {
    this.resizeListeners.add(listener);
    return () => this.resizeListeners.delete(listener);
  }
  /**
   * Subscribes to double-click/double-tap activation. Detected from the
   * canvas's own pointer pipeline (two quick taps that didn't travel and
   * weren't part of a pinch), so it behaves identically for mouse and
   * touch — unlike the DOM `dblclick` event, which some mobile browsers
   * withhold. The point is in container coordinates, ready to be passed
   * to zoomTo/zoomBy as the focal point. Only fires while interaction is
   * enabled. Returns an unsubscribe function.
   */
  onDoubleActivate(listener) {
    this.doubleActivateListeners.add(listener);
    return () => this.doubleActivateListeners.delete(listener);
  }
  // ── Lifecycle ──────────────────────────────────────────────────────────
  /** Detaches observers/listeners and removes the stage from the DOM. */
  destroy() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.interactionEnabled) {
      this.detachInteractionListeners();
    }
    this.viewportListeners.clear();
    this.resizeListeners.clear();
    this.doubleActivateListeners.clear();
    this.layers.clear();
    this.container.removeChild(this.stage);
  }
  // ── Internals ──────────────────────────────────────────────────────────
  /** The zoom bounds currently in force — absolute, or fit-relative when setRelativeScaleBounds is active. */
  effectiveScaleBounds() {
    if (this.relativeScaleBounds && this.contentSize) {
      const fitScale = computeFitTransform(this.contentSize, this.containerSize).scale;
      if (fitScale > 0) {
        return { min: fitScale * this.relativeScaleBounds.min, max: fitScale * this.relativeScaleBounds.max };
      }
    }
    return { min: this.minScale, max: this.maxScale };
  }
  setTransform(transform) {
    this.transform = this.constrainToContainer && this.contentSize ? constrainTransformToContainer(transform, this.contentSize, this.containerSize) : transform;
    const applied = this.transform;
    this.stage.style.transform = `translate(${applied.offset.x}px, ${applied.offset.y}px) scale(${applied.scale})`;
    const event = { transform: applied, containerSize: this.containerSize };
    this.viewportListeners.forEach((listener) => listener(event));
    this.layers.forEach((layer) => layer.onViewportChange?.(applied));
  }
  attachResizeObserver() {
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      this.containerSize = { width, height };
      if (!this.hasCustomView && this.contentSize) {
        const { min, max } = this.effectiveScaleBounds();
        this.setTransform(computeClampedFitTransform(this.contentSize, this.containerSize, min, max));
      } else if (this.constrainToContainer && this.contentSize) {
        this.setTransform(this.transform);
      }
      this.resizeListeners.forEach((listener) => listener(this.containerSize));
      this.layers.forEach((layer) => layer.onResize?.(this.containerSize));
    });
    this.resizeObserver.observe(this.container);
  }
  handleWheel = (event) => {
    event.preventDefault();
    const focalPoint = this.eventToContainerPoint(event);
    const factor = Math.exp(-event.deltaY * WHEEL_ZOOM_SENSITIVITY);
    this.zoomBy(factor, focalPoint);
  };
  handlePointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    this.container.setPointerCapture(event.pointerId);
    const point = this.eventToContainerPoint(event);
    this.activePointers.set(event.pointerId, point);
    this.pointerDownPoints.set(event.pointerId, point);
    if (this.activePointers.size === 2) {
      this.gestureHadMultiplePointers = true;
      const [a, b] = Array.from(this.activePointers.values());
      this.pinchStartDistance = distanceBetween(a, b);
      this.pinchStartScale = this.transform.scale;
    }
  };
  handlePointerMove = (event) => {
    const previousPoint = this.activePointers.get(event.pointerId);
    if (!previousPoint) return;
    const nextPoint = this.eventToContainerPoint(event);
    this.activePointers.set(event.pointerId, nextPoint);
    if (this.activePointers.size === 1) {
      this.panBy({ x: nextPoint.x - previousPoint.x, y: nextPoint.y - previousPoint.y });
      return;
    }
    if (this.activePointers.size === 2 && this.pinchStartDistance > 0) {
      const [a, b] = Array.from(this.activePointers.values());
      const currentDistance = distanceBetween(a, b);
      const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      this.zoomTo(this.pinchStartScale * (currentDistance / this.pinchStartDistance), midpoint);
    }
  };
  /** pointerup only — a completed press can be a tap; cancel/leave never are. */
  handlePointerUp = (event) => {
    this.detectDoubleActivate(event);
    this.handlePointerEnd(event);
  };
  handlePointerEnd = (event) => {
    this.pointerDownPoints.delete(event.pointerId);
    this.activePointers.delete(event.pointerId);
    if (this.activePointers.size < 2) {
      this.pinchStartDistance = 0;
    }
    if (this.activePointers.size === 0) {
      this.gestureHadMultiplePointers = false;
    }
  };
  /** Feeds the double-tap/double-click detector — see onDoubleActivate. */
  detectDoubleActivate(event) {
    const downPoint = this.pointerDownPoints.get(event.pointerId);
    if (!downPoint || this.gestureHadMultiplePointers) return;
    const upPoint = this.eventToContainerPoint(event);
    if (distanceBetween(downPoint, upPoint) > TAP_MAX_TRAVEL_PX) return;
    const isSecondTap = this.lastTapPoint !== null && event.timeStamp - this.lastTapTime <= DOUBLE_TAP_MAX_DELAY_MS && distanceBetween(this.lastTapPoint, upPoint) <= DOUBLE_TAP_MAX_DISTANCE_PX;
    if (isSecondTap) {
      this.lastTapPoint = null;
      this.lastTapTime = 0;
      this.doubleActivateListeners.forEach((listener) => listener(upPoint));
    } else {
      this.lastTapPoint = upPoint;
      this.lastTapTime = event.timeStamp;
    }
  }
  attachInteractionListeners() {
    this.container.addEventListener("wheel", this.handleWheel, { passive: false });
    this.container.addEventListener("pointerdown", this.handlePointerDown);
    this.container.addEventListener("pointermove", this.handlePointerMove);
    this.container.addEventListener("pointerup", this.handlePointerUp);
    this.container.addEventListener("pointercancel", this.handlePointerEnd);
    this.container.addEventListener("pointerleave", this.handlePointerEnd);
  }
  detachInteractionListeners() {
    this.container.removeEventListener("wheel", this.handleWheel);
    this.container.removeEventListener("pointerdown", this.handlePointerDown);
    this.container.removeEventListener("pointermove", this.handlePointerMove);
    this.container.removeEventListener("pointerup", this.handlePointerUp);
    this.container.removeEventListener("pointercancel", this.handlePointerEnd);
    this.container.removeEventListener("pointerleave", this.handlePointerEnd);
  }
  eventToContainerPoint(event) {
    const rect = this.container.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }
};
function distanceBetween(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// viewport/image-layer.ts
var IMAGE_LAYER_ID = "image";
var IMAGE_LAYER_Z_INDEX = 0;
var ImageLayer = class {
  id = IMAGE_LAYER_ID;
  zIndex = IMAGE_LAYER_Z_INDEX;
  element;
  objectUrl;
  constructor(image) {
    this.element = image.element;
    this.objectUrl = image.objectUrl;
    this.element.style.width = `${image.dimensions.width}px`;
    this.element.style.height = `${image.dimensions.height}px`;
    this.element.style.display = "block";
    this.element.draggable = false;
    this.element.style.userSelect = "none";
    this.element.style.setProperty("-webkit-user-drag", "none");
  }
  /** Revokes the backing object URL. Call after removing this layer from its PreviewCanvas. */
  destroy() {
    URL.revokeObjectURL(this.objectUrl);
  }
};
function displayImageInCanvas(canvas, image) {
  const previous = canvas.getLayer(IMAGE_LAYER_ID);
  if (previous instanceof ImageLayer) {
    canvas.removeLayer(IMAGE_LAYER_ID);
    previous.destroy();
  }
  canvas.setContentSize(image.dimensions);
  const layer = new ImageLayer(image);
  canvas.addLayer(layer);
  return layer;
}

// viewport/placement-layer.ts
var SVG_NS = "http://www.w3.org/2000/svg";
var PLACEMENT_LAYER_ID = "placements";
var PLACEMENT_LAYER_Z_INDEX = 10;
var ENHANCED_HANDLE_RADIUS_PX = 10;
var ENHANCED_CORNER_HANDLE_RADIUS_PX = 12;
var DEFAULT_PLACEMENT_THEME = {
  idleFill: "rgba(255, 255, 255, 0.12)",
  idleStroke: "rgba(255, 255, 255, 0.55)",
  idleStrokeWidth: 1.5,
  hoverFill: "rgba(255, 255, 255, 0.22)",
  hoverStroke: "rgba(255, 255, 255, 0.95)",
  hoverStrokeWidth: 2,
  selectedFill: "rgba(91, 157, 255, 0.32)",
  selectedStroke: "#5b9dff",
  selectedStrokeWidth: 3,
  boundingBoxDash: "5 4",
  handleFill: "#ffffff",
  handleStroke: "#5b9dff"
};
var PlacementLayer = class {
  id = PLACEMENT_LAYER_ID;
  zIndex = PLACEMENT_LAYER_Z_INDEX;
  element;
  theme;
  cornerHandlesEnabled;
  unsubscribe;
  /**
   * `cornerHandlesEnabled` opts into the four corner resize handles (see
   * `getCornerHandlePositions`) plus larger handle sizing generally —
   * defaults to false, reproducing this class's original handle
   * rendering exactly, so a host that never sets it (DHA Wheel Studio)
   * is unaffected. Only the internal Admin Tool currently opts in.
   */
  constructor(store, contentWidth, contentHeight, theme = DEFAULT_PLACEMENT_THEME, cornerHandlesEnabled = false) {
    this.theme = theme;
    this.cornerHandlesEnabled = cornerHandlesEnabled;
    this.element = document.createElementNS(SVG_NS, "svg");
    this.element.setAttribute("width", String(contentWidth));
    this.element.setAttribute("height", String(contentHeight));
    this.element.style.display = "block";
    this.element.style.pointerEvents = "none";
    this.element.style.overflow = "visible";
    this.unsubscribe = store.subscribe((state) => this.render(state));
    this.render({ placements: store.getPlacements(), selectedId: store.getSelectedId(), hoveredId: store.getHoveredId() });
  }
  /** Stops listening to the store. Call after removing this layer from its PreviewCanvas. */
  destroy() {
    this.unsubscribe();
  }
  render(state) {
    while (this.element.firstChild) this.element.removeChild(this.element.firstChild);
    for (const placement of state.placements) {
      const isSelected = placement.id === state.selectedId;
      const isHovered = placement.id === state.hoveredId && !isSelected;
      this.element.appendChild(this.renderPlacement(placement, isSelected, isHovered));
    }
  }
  renderPlacement(placement, isSelected, isHovered) {
    const group = document.createElementNS(SVG_NS, "g");
    const { cx, cy, majorAxis, minorAxis, angle } = placement.geometry;
    const ellipse = document.createElementNS(SVG_NS, "ellipse");
    ellipse.setAttribute("cx", String(cx));
    ellipse.setAttribute("cy", String(cy));
    ellipse.setAttribute("rx", String(Math.max(0, majorAxis / 2)));
    ellipse.setAttribute("ry", String(Math.max(0, minorAxis / 2)));
    ellipse.setAttribute("transform", `rotate(${angle * 180 / Math.PI} ${cx} ${cy})`);
    if (isSelected) {
      ellipse.setAttribute("fill", this.theme.selectedFill);
      ellipse.setAttribute("stroke", this.theme.selectedStroke);
      ellipse.setAttribute("stroke-width", String(this.theme.selectedStrokeWidth));
    } else if (isHovered) {
      ellipse.setAttribute("fill", this.theme.hoverFill);
      ellipse.setAttribute("stroke", this.theme.hoverStroke);
      ellipse.setAttribute("stroke-width", String(this.theme.hoverStrokeWidth));
    } else {
      ellipse.setAttribute("fill", this.theme.idleFill);
      ellipse.setAttribute("stroke", this.theme.idleStroke);
      ellipse.setAttribute("stroke-width", String(this.theme.idleStrokeWidth));
    }
    ellipse.setAttribute("vector-effect", "non-scaling-stroke");
    if (this.theme.glow) ellipse.style.filter = this.theme.glow;
    group.appendChild(ellipse);
    if (isSelected) {
      const edgeRadius = this.cornerHandlesEnabled ? ENHANCED_HANDLE_RADIUS_PX : HANDLE_RADIUS_PX;
      group.appendChild(this.renderBoundingBox(placement.geometry));
      group.appendChild(this.renderHandle(getMajorAxisHandlePosition(placement.geometry), edgeRadius));
      group.appendChild(this.renderHandle(getMajorAxisHandlePositionNegative(placement.geometry), edgeRadius));
      group.appendChild(this.renderHandle(getMinorAxisHandlePosition(placement.geometry), edgeRadius));
      group.appendChild(this.renderHandle(getMinorAxisHandlePositionNegative(placement.geometry), edgeRadius));
      if (this.cornerHandlesEnabled) {
        for (const corner of getCornerHandlePositions(placement.geometry)) {
          group.appendChild(this.renderHandle(corner, ENHANCED_CORNER_HANDLE_RADIUS_PX));
        }
      }
    }
    return group;
  }
  /**
   * The selection/resize affordance: a rectangle around the ellipse's
   * bounding box, so resizing reads as "drag the edge of the box" — a
   * more standard, discoverable pattern than isolated floating handles.
   * Uses the same rotation transform as the ellipse itself, so it stays
   * aligned even for a (currently unreachable via manual placement)
   * non-zero angle.
   */
  renderBoundingBox(geometry) {
    const { cx, cy, majorAxis, minorAxis, angle } = geometry;
    const rx = Math.max(0, majorAxis / 2);
    const ry = Math.max(0, minorAxis / 2);
    const rect = document.createElementNS(SVG_NS, "rect");
    rect.setAttribute("x", String(cx - rx));
    rect.setAttribute("y", String(cy - ry));
    rect.setAttribute("width", String(rx * 2));
    rect.setAttribute("height", String(ry * 2));
    rect.setAttribute("transform", `rotate(${angle * 180 / Math.PI} ${cx} ${cy})`);
    rect.setAttribute("fill", "none");
    rect.setAttribute("stroke", this.theme.selectedStroke);
    rect.setAttribute("stroke-width", "1.5");
    if (this.theme.boundingBoxDash !== "none") rect.setAttribute("stroke-dasharray", this.theme.boundingBoxDash);
    rect.setAttribute("vector-effect", "non-scaling-stroke");
    return rect;
  }
  renderHandle(position, radius) {
    const handle = document.createElementNS(SVG_NS, "circle");
    handle.setAttribute("cx", String(position.x));
    handle.setAttribute("cy", String(position.y));
    handle.setAttribute("r", String(radius));
    handle.setAttribute("fill", this.theme.handleFill);
    handle.setAttribute("stroke", this.theme.handleStroke);
    handle.setAttribute("stroke-width", "2");
    handle.setAttribute("vector-effect", "non-scaling-stroke");
    return handle;
  }
};
function displayPlacementsInCanvas(canvas, store, theme, cornerHandlesEnabled = false) {
  const contentSize = canvas.getContentSize();
  if (!contentSize) {
    throw new Error("displayPlacementsInCanvas requires an image to be displayed first (canvas.getContentSize() is null).");
  }
  const previous = canvas.getLayer(PLACEMENT_LAYER_ID);
  if (previous instanceof PlacementLayer) {
    canvas.removeLayer(PLACEMENT_LAYER_ID);
    previous.destroy();
  }
  const layer = new PlacementLayer(store, contentSize.width, contentSize.height, theme, cornerHandlesEnabled);
  canvas.addLayer(layer);
  return layer;
}

// viewport/wheel-layer.ts
var SVG_NS2 = "http://www.w3.org/2000/svg";
var WHEEL_LAYER_ID = "wheel-textures";
var WHEEL_LAYER_Z_INDEX = 5;
var DEFAULT_WHEEL_FILTER_OPTIONS = {
  brightness: 0.94,
  contrast: 1.06,
  saturation: 0.9,
  blurPx: 0.5,
  opacity: 0.97,
  shadowStrength: 0.35,
  autoToneMatchEnabled: true,
  autoToneStrength: 0.5
};
function buildWheelFilterCss(options) {
  const parts = [`brightness(${options.brightness})`, `contrast(${options.contrast})`, `saturate(${options.saturation})`];
  if (options.blurPx > 0) parts.push(`blur(${options.blurPx}px)`);
  if (options.shadowStrength > 0) {
    const blur = (4 + options.shadowStrength * 6).toFixed(1);
    const offsetY = (2 + options.shadowStrength * 3).toFixed(1);
    const alpha = Math.min(0.6, options.shadowStrength * 0.6).toFixed(2);
    parts.push(`drop-shadow(0 ${offsetY}px ${blur}px rgba(0, 0, 0, ${alpha}))`);
  }
  return { filter: parts.join(" "), opacity: String(options.opacity) };
}
function buildWheelImageTransform(cx, cy, angleDeg, mirrored) {
  const rotate = `rotate(${angleDeg} ${cx} ${cy})`;
  if (!mirrored) return rotate;
  return `${rotate} translate(${2 * cx} 0) scale(-1 1)`;
}
var WheelLayer = class {
  id = WHEEL_LAYER_ID;
  zIndex = WHEEL_LAYER_Z_INDEX;
  element;
  library;
  placements;
  filterOptions;
  mirrored;
  pixelSource;
  toneDebug = /* @__PURE__ */ new Map();
  unsubscribePlacements;
  unsubscribeLibrary;
  constructor(store, library, contentWidth, contentHeight, filterOptions = DEFAULT_WHEEL_FILTER_OPTIONS, pixelSource = null, mirrored = false) {
    this.library = library;
    this.placements = store.getPlacements();
    this.filterOptions = filterOptions;
    this.mirrored = mirrored;
    this.pixelSource = pixelSource;
    this.element = document.createElementNS(SVG_NS2, "svg");
    this.element.setAttribute("width", String(contentWidth));
    this.element.setAttribute("height", String(contentHeight));
    this.element.style.display = "block";
    this.element.style.pointerEvents = "none";
    this.element.style.overflow = "visible";
    this.unsubscribePlacements = store.subscribe((state) => {
      this.placements = state.placements;
      this.render();
    });
    this.unsubscribeLibrary = library.subscribe(() => this.render());
    this.render();
  }
  /** Stops listening to the store and the library. Call after removing this layer from its PreviewCanvas. */
  destroy() {
    this.unsubscribePlacements();
    this.unsubscribeLibrary();
  }
  /** Read-only snapshot of the last render's auto tone-match sampling, one entry per current placement. Admin Tool debug display only. */
  getToneDebugInfo() {
    return Array.from(this.toneDebug.values());
  }
  render() {
    while (this.element.firstChild) this.element.removeChild(this.element.firstChild);
    this.toneDebug.clear();
    const texture = this.library.getSelectedTexture();
    if (!texture) return;
    for (const placement of this.placements) {
      this.element.appendChild(this.renderWheelImage(placement, texture.element.src));
    }
  }
  /**
   * Resolves this placement's effective filter — the shared `filterOptions`
   * as-is, or (when auto tone-match is on and a photo pixel source exists)
   * nudged toward the photo's own local brightness/contrast around this
   * specific placement. Per-placement rather than once per render so two
   * wheels in the same photo but different local lighting (e.g. one in
   * direct sun, one in shadow) can each match their own surroundings.
   */
  resolveFilterForPlacement(placement) {
    if (!this.filterOptions.autoToneMatchEnabled || !this.pixelSource) return this.filterOptions;
    const { cx, cy, majorAxis, minorAxis, angle } = placement.geometry;
    const scale = this.pixelSource.scale;
    const stats = sampleSceneToneStats(this.pixelSource, cx * scale, cy * scale, majorAxis / 2 * scale, minorAxis / 2 * scale, angle);
    const effective = resolveEffectiveWheelFilter(this.filterOptions, stats);
    this.toneDebug.set(placement.id, {
      placementId: placement.id,
      meanLuminance: stats ? stats.meanLuminance : null,
      brightness: effective.brightness,
      contrast: effective.contrast
    });
    return effective;
  }
  renderWheelImage(placement, textureSrc) {
    const { cx, cy, majorAxis, minorAxis, angle } = placement.geometry;
    const width = Math.max(0, majorAxis);
    const height = Math.max(0, minorAxis);
    const image = document.createElementNS(SVG_NS2, "image");
    image.setAttribute("href", textureSrc);
    image.setAttribute("x", String(cx - width / 2));
    image.setAttribute("y", String(cy - height / 2));
    image.setAttribute("width", String(width));
    image.setAttribute("height", String(height));
    image.setAttribute("transform", buildWheelImageTransform(cx, cy, angle * 180 / Math.PI, this.mirrored));
    image.setAttribute("preserveAspectRatio", "none");
    const { filter, opacity } = buildWheelFilterCss(this.resolveFilterForPlacement(placement));
    image.style.filter = filter;
    image.style.opacity = opacity;
    return image;
  }
};
function displayWheelInCanvas(canvas, store, library, filterOptions = DEFAULT_WHEEL_FILTER_OPTIONS, pixelSource = null, mirrored = false) {
  const contentSize = canvas.getContentSize();
  if (!contentSize) {
    throw new Error("displayWheelInCanvas requires an image to be displayed first (canvas.getContentSize() is null).");
  }
  const previous = canvas.getLayer(WHEEL_LAYER_ID);
  if (previous instanceof WheelLayer) {
    canvas.removeLayer(WHEEL_LAYER_ID);
    previous.destroy();
  }
  const layer = new WheelLayer(store, library, contentSize.width, contentSize.height, filterOptions, pixelSource, mirrored);
  canvas.addLayer(layer);
  return layer;
}

// viewport/wheel-backing-layer.ts
var SVG_NS3 = "http://www.w3.org/2000/svg";
var WHEEL_BACKING_LAYER_ID = "wheel-backing";
var WHEEL_BACKING_LAYER_Z_INDEX = 3;
var RECESS_SCALE = 0.94;
var BARREL_INNER = "#121212";
var BARREL_MID = "#050505";
var BARREL_OUTER = "#000000";
var WheelBackingLayer = class {
  id = WHEEL_BACKING_LAYER_ID;
  zIndex = WHEEL_BACKING_LAYER_Z_INDEX;
  element;
  placements;
  options;
  unsubscribe;
  instanceId = crypto.randomUUID();
  constructor(store, pixelSource, options, contentWidth, contentHeight) {
    this.placements = store.getPlacements();
    void pixelSource;
    this.options = options;
    this.element = document.createElementNS(SVG_NS3, "svg");
    this.element.setAttribute("width", String(contentWidth));
    this.element.setAttribute("height", String(contentHeight));
    this.element.style.display = "block";
    this.element.style.pointerEvents = "none";
    this.element.style.overflow = "visible";
    this.unsubscribe = store.subscribe((state) => {
      this.placements = state.placements;
      this.render();
    });
    this.render();
  }
  /** Stops listening to the store. Call after removing this layer from its PreviewCanvas. */
  destroy() {
    this.unsubscribe();
  }
  render() {
    for (const child of Array.from(this.element.children)) {
      this.element.removeChild(child);
    }
    for (const placement of this.placements) {
      const { cx, cy, majorAxis, minorAxis, angle } = placement.geometry;
      const rx = Math.max(0, majorAxis) / 2;
      const ry = Math.max(0, minorAxis) / 2;
      this.element.appendChild(this.renderRecess(placement.id, cx, cy, rx, ry, angle));
      if (this.options.debug) {
        this.element.appendChild(this.renderDebugOverlay(cx, cy, rx, ry, angle));
      }
    }
  }
  renderRecess(placementId, cx, cy, rx, ry, angle) {
    const rotationDeg = angle * 180 / Math.PI;
    const safeId = `${this.instanceId}-${placementId}`.replace(/[^a-zA-Z0-9_-]/g, "-");
    const gradientId = `wheel-recess-gradient-${safeId}`;
    const clipId = `wheel-recess-clip-${safeId}`;
    const filterId = `wheel-recess-inner-shadow-${safeId}`;
    const group = document.createElementNS(SVG_NS3, "g");
    const defs = document.createElementNS(SVG_NS3, "defs");
    const gradient = document.createElementNS(SVG_NS3, "radialGradient");
    gradient.setAttribute("id", gradientId);
    gradient.setAttribute("cx", "50%");
    gradient.setAttribute("cy", "46%");
    gradient.setAttribute("r", "58%");
    for (const [offset, color] of [
      ["0%", BARREL_INNER],
      ["42%", BARREL_MID],
      ["76%", BARREL_OUTER],
      ["100%", BARREL_OUTER]
    ]) {
      const stop = document.createElementNS(SVG_NS3, "stop");
      stop.setAttribute("offset", offset);
      stop.setAttribute("stop-color", color);
      gradient.appendChild(stop);
    }
    defs.appendChild(gradient);
    const clipPath = document.createElementNS(SVG_NS3, "clipPath");
    clipPath.setAttribute("id", clipId);
    const clipEllipse = document.createElementNS(SVG_NS3, "ellipse");
    clipEllipse.setAttribute("cx", String(cx));
    clipEllipse.setAttribute("cy", String(cy));
    clipEllipse.setAttribute("rx", String(rx * RECESS_SCALE));
    clipEllipse.setAttribute("ry", String(ry * RECESS_SCALE));
    clipEllipse.setAttribute("transform", `rotate(${rotationDeg} ${cx} ${cy})`);
    clipPath.appendChild(clipEllipse);
    defs.appendChild(clipPath);
    const filter = document.createElementNS(SVG_NS3, "filter");
    filter.setAttribute("id", filterId);
    filter.setAttribute("x", "-20%");
    filter.setAttribute("y", "-20%");
    filter.setAttribute("width", "140%");
    filter.setAttribute("height", "140%");
    const blur = document.createElementNS(SVG_NS3, "feGaussianBlur");
    blur.setAttribute("stdDeviation", String(Math.max(2, Math.min(rx, ry) * 0.035)));
    filter.appendChild(blur);
    defs.appendChild(filter);
    group.appendChild(defs);
    const recess = document.createElementNS(SVG_NS3, "g");
    recess.setAttribute("clip-path", `url(#${clipId})`);
    const barrel = document.createElementNS(SVG_NS3, "ellipse");
    barrel.setAttribute("cx", String(cx));
    barrel.setAttribute("cy", String(cy));
    barrel.setAttribute("rx", String(rx * RECESS_SCALE));
    barrel.setAttribute("ry", String(ry * RECESS_SCALE));
    barrel.setAttribute("transform", `rotate(${rotationDeg} ${cx} ${cy})`);
    barrel.setAttribute("fill", `url(#${gradientId})`);
    recess.appendChild(barrel);
    const lowerShadow = document.createElementNS(SVG_NS3, "ellipse");
    lowerShadow.setAttribute("cx", String(cx));
    lowerShadow.setAttribute("cy", String(cy + ry * 0.18));
    lowerShadow.setAttribute("rx", String(rx * 0.76));
    lowerShadow.setAttribute("ry", String(ry * 0.48));
    lowerShadow.setAttribute("transform", `rotate(${rotationDeg} ${cx} ${cy})`);
    lowerShadow.setAttribute("fill", "#000000");
    lowerShadow.setAttribute("fill-opacity", "0.34");
    lowerShadow.setAttribute("filter", `url(#${filterId})`);
    recess.appendChild(lowerShadow);
    const lipShadow = document.createElementNS(SVG_NS3, "ellipse");
    lipShadow.setAttribute("cx", String(cx));
    lipShadow.setAttribute("cy", String(cy));
    lipShadow.setAttribute("rx", String(rx * (RECESS_SCALE - 0.025)));
    lipShadow.setAttribute("ry", String(ry * (RECESS_SCALE - 0.025)));
    lipShadow.setAttribute("transform", `rotate(${rotationDeg} ${cx} ${cy})`);
    lipShadow.setAttribute("fill", "none");
    lipShadow.setAttribute("stroke", "#000000");
    lipShadow.setAttribute("stroke-opacity", "0.82");
    lipShadow.setAttribute("stroke-width", String(Math.max(4, Math.min(rx, ry) * 0.075)));
    recess.appendChild(lipShadow);
    group.appendChild(recess);
    return group;
  }
  renderDebugOverlay(cx, cy, rx, ry, angle) {
    const group = document.createElementNS(SVG_NS3, "g");
    const rotationDeg = angle * 180 / Math.PI;
    const clip = document.createElementNS(SVG_NS3, "ellipse");
    clip.setAttribute("cx", String(cx));
    clip.setAttribute("cy", String(cy));
    clip.setAttribute("rx", String(rx * RECESS_SCALE));
    clip.setAttribute("ry", String(ry * RECESS_SCALE));
    clip.setAttribute("transform", `rotate(${rotationDeg} ${cx} ${cy})`);
    clip.setAttribute("fill", "none");
    clip.setAttribute("stroke", "#39ff14");
    clip.setAttribute("stroke-width", "2");
    clip.setAttribute("stroke-dasharray", "6 6");
    group.appendChild(clip);
    const center = document.createElementNS(SVG_NS3, "ellipse");
    center.setAttribute("cx", String(cx));
    center.setAttribute("cy", String(cy));
    center.setAttribute("rx", String(rx * 0.42));
    center.setAttribute("ry", String(ry * 0.42));
    center.setAttribute("transform", `rotate(${rotationDeg} ${cx} ${cy})`);
    center.setAttribute("fill", "none");
    center.setAttribute("stroke", "#39ff14");
    center.setAttribute("stroke-width", "1.5");
    center.setAttribute("stroke-opacity", "0.72");
    group.appendChild(center);
    return group;
  }
};
function displayWheelBackingInCanvas(canvas, store, pixelSource, options = {}) {
  const contentSize = canvas.getContentSize();
  if (!contentSize) {
    throw new Error("displayWheelBackingInCanvas requires an image to be displayed first (canvas.getContentSize() is null).");
  }
  const previous = canvas.getLayer(WHEEL_BACKING_LAYER_ID);
  if (previous instanceof WheelBackingLayer) {
    canvas.removeLayer(WHEEL_BACKING_LAYER_ID);
    previous.destroy();
  }
  const layer = new WheelBackingLayer(store, pixelSource, options, contentSize.width, contentSize.height);
  canvas.addLayer(layer);
  return layer;
}

// core/state-store.ts
var VisualizerStateStore = class {
  /**
   * Returns the current immutable state snapshot.
   */
  getState() {
    throw new Error("Not implemented \u2014 see docs/wheel-visualizer-architecture.md \xA76");
  }
  /**
   * Subscribes to state changes. Returns an unsubscribe function.
   */
  subscribe(_listener) {
    throw new Error("Not implemented \u2014 see docs/wheel-visualizer-architecture.md \xA76");
  }
};

// core/visualizer.ts
var RENDER_LAYER_IDS = ["wheel-backing", "wheel-textures"];
var DEFAULT_GUIDE_COUNT = 2;
var WheelVisualizer = class {
  placementStore = new PlacementStore();
  wheelLibrary = new WheelLibrary();
  stateStore;
  ringTheme;
  cornerHandlesEnabled;
  keyboardAdjustEnabled;
  guideEditingEnabled;
  viewportInteractionEnabled;
  wheelBackingEnabled;
  previewCanvas = null;
  placementInteraction = null;
  currentImage = null;
  pixelSource = null;
  hasAppliedWheel = false;
  targetGuideCount = DEFAULT_GUIDE_COUNT;
  backingOptions = {};
  filterOptions = DEFAULT_WHEEL_FILTER_OPTIONS;
  mirrored = false;
  currentWheelLayer = null;
  constructor(options = {}) {
    this.stateStore = new VisualizerStateStore();
    this.ringTheme = options.ringTheme;
    this.cornerHandlesEnabled = options.cornerHandlesEnabled ?? false;
    this.keyboardAdjustEnabled = options.keyboardAdjustEnabled ?? false;
    this.guideEditingEnabled = options.guideEditingEnabled ?? true;
    this.viewportInteractionEnabled = options.viewportInteractionEnabled ?? true;
    this.wheelBackingEnabled = options.wheelBackingEnabled ?? true;
  }
  /** Returns the underlying state store. Reserved for a future milestone — see {@link VisualizerStateStore}'s own doc comment. */
  getStateStore() {
    return this.stateStore;
  }
  /**
   * Mounts the visualizer into a host DOM element: constructs the
   * `PreviewCanvas` and `PlacementInteraction` (manual ring drag/resize)
   * against it. The same element hosts both — see either host's call
   * site for the existing convention.
   */
  mount(element) {
    if (this.previewCanvas) {
      throw new Error("WheelVisualizer is already mounted \u2014 call destroy() before mounting again.");
    }
    this.previewCanvas = new PreviewCanvas(element, { interactionEnabled: this.viewportInteractionEnabled });
    if (this.guideEditingEnabled) {
      this.placementInteraction = new PlacementInteraction(element, this.previewCanvas, this.placementStore, this.cornerHandlesEnabled, this.keyboardAdjustEnabled);
    }
  }
  /** The mounted `PreviewCanvas`. Throws if called before `mount()`. */
  getPreviewCanvas() {
    return this.requirePreviewCanvas();
  }
  /**
   * Tears down the visualizer and releases any resources (textures, event
   * listeners) it acquired.
   */
  destroy() {
    this.previewCanvas?.destroy();
    this.placementInteraction?.destroy();
    this.previewCanvas = null;
    this.placementInteraction = null;
  }
  getCurrentImage() {
    return this.currentImage;
  }
  hasWheelApplied() {
    return this.hasAppliedWheel;
  }
  getTargetGuideCount() {
    return this.targetGuideCount;
  }
  /**
   * Records the current photo without touching the canvas — cheap and
   * synchronous, safe to call before the host's canvas container is
   * visible. Exists so a *staged* host (one whose canvas container
   * starts hidden, e.g. DHA Wheel Studio's upload step) can update
   * `getCurrentImage()` — and therefore whatever "do we have a photo
   * yet" check gates revealing that stage — *before* revealing the
   * container and calling `loadImage()`. `loadImage()` calls this
   * itself, so a host whose canvas is always visible (the internal
   * Admin Tool) never needs to call it directly.
   */
  setCurrentImage(image) {
    this.currentImage = image;
    this.hasAppliedWheel = false;
  }
  /**
   * Mounts a newly loaded photo: displays it, resets rings to starter
   * guides (clearing any from the previous photo, since guide geometry
   * is tied to a specific image's pixel space), and clears "applied
   * wheel" status. Call this from the host's `ImageUpload.onImageLoaded`
   * — if the host's canvas container was hidden (e.g. a staged UI not
   * yet on the photo step), reveal it *before* calling this (using
   * `setCurrentImage()` first if revealing it depends on
   * `getCurrentImage()` being already set), since the canvas remeasures
   * its container synchronously as part of this call.
   */
  loadImage(image) {
    const canvas = this.requirePreviewCanvas();
    this.setCurrentImage(image);
    canvas.remeasureContainer();
    displayImageInCanvas(canvas, image);
    const contentSize = canvas.getContentSize();
    try {
      this.pixelSource = contentSize ? createPixelSource(image.element, contentSize.width, contentSize.height) : null;
    } catch {
      this.pixelSource = null;
    }
    this.placementStore.clear();
    if (this.guideEditingEnabled) {
      displayPlacementsInCanvas(canvas, this.placementStore, this.ringTheme ?? DEFAULT_PLACEMENT_THEME, this.cornerHandlesEnabled);
    }
    this.setGuideCount(this.targetGuideCount);
    this.renderAppliedWheel();
  }
  /**
   * Mounts a pre-authored scene: the image plus its own wheel guides,
   * replacing the starter guides `loadImage` seeds. This is the curated
   * counterpart of the upload flow — a scene-based host (DHA Vehicle
   * Showroom) calls this instead of letting the user position rings; an
   * upload host's detection integration effectively does the same thing
   * by mutating `placementStore` directly. Any applied-wheel status is
   * cleared by the image swap, exactly as with `loadImage` — re-apply
   * via `applyWheel` afterward if the host wants the selection to
   * survive a scene switch.
   */
  loadScene(image, scene) {
    this.loadImage(image);
    this.placementStore.clear();
    for (const geometry of scene.guides) {
      this.placementStore.add({ imageId: image.id, geometry, source: "manual" });
    }
    this.renderAppliedWheel();
  }
  /** Sets how many wheel guide rings are shown (clamped to 1–2), adding/removing starter guides as needed. A no-op before a photo is loaded. */
  setGuideCount(count) {
    const canvas = this.previewCanvas;
    if (!canvas || !this.currentImage) return;
    const clampedCount = Math.max(1, Math.min(2, count));
    this.targetGuideCount = clampedCount;
    const rings = this.placementStore.getPlacements();
    if (rings.length > clampedCount) {
      for (const ring of rings.slice(clampedCount).reverse()) {
        this.placementStore.remove(ring.id);
      }
    } else {
      for (let i = rings.length; i < clampedCount; i++) {
        this.placementStore.add({
          imageId: this.currentImage.id,
          geometry: createStarterGuideGeometry(canvas.getContentSize(), i, clampedCount),
          source: "manual"
        });
      }
    }
    this.renderAppliedWheel();
  }
  /** Selects a wheel in the library and marks it "applied" (rendered onto the current rings). A no-op if `wheelId` doesn't exist in the library (see `WheelLibrary.select`). */
  applyWheel(wheelId) {
    this.hasAppliedWheel = true;
    this.wheelLibrary.select(wheelId);
    this.renderAppliedWheel();
  }
  /**
   * Sets the wheel-backing layer's rendering options (e.g. the internal
   * app's debug-sampling toggle) and immediately re-renders so the
   * change is visible right away.
   */
  setWheelBackingOptions(options) {
    this.backingOptions = options;
    this.renderAppliedWheel();
  }
  /**
   * Sets the wheel-texture realism filter (brightness/contrast/
   * saturation/blur/opacity/shadow — see `viewport/wheel-layer.ts`'s
   * `WheelFilterOptions`) and immediately re-renders. Defaults to
   * `DEFAULT_WHEEL_FILTER_OPTIONS`, so a host that never calls this (DHA
   * Wheel Studio) still gets the realism baseline automatically; only
   * the Admin Tool's Realism panel calls this, to tune away from it.
   */
  setWheelFilterOptions(options) {
    this.filterOptions = options;
    this.renderAppliedWheel();
  }
  /** Whether the wheel texture is currently mirrored horizontally — see `setWheelMirrored`. */
  isWheelMirrored() {
    return this.mirrored;
  }
  /**
   * Horizontally mirrors (left ↔ right) every rendered wheel texture, or
   * restores the original orientation. Purely visual — placement
   * position/size, finish selection, and the realism filter are
   * unaffected — and persists across `applyWheel`/`loadImage`/`loadScene`
   * calls (a class field alongside `filterOptions`/`backingOptions`,
   * never reset by them) since the whole point is to survive switching
   * wheel designs or vehicle scenes.
   */
  setWheelMirrored(mirrored) {
    this.mirrored = mirrored;
    this.renderAppliedWheel();
  }
  /**
   * Read-only debug snapshot of the current wheel layer's last render —
   * one entry per placement, populated only when auto tone-match is on.
   * Admin Tool "Realism" debug display only; never used by rendering
   * itself, and empty whenever no wheel is applied.
   */
  getWheelToneDebugInfo() {
    return this.currentWheelLayer?.getToneDebugInfo() ?? [];
  }
  /**
   * (Re)renders the currently-applied wheel onto the current rings, or
   * removes any previously-rendered wheel layers if none is applied yet
   * (or no photo is loaded). Public because callers that mutate
   * `placementStore` directly — a host's own detection integration, for
   * one — need to trigger a re-render afterward without re-running the
   * rest of `loadImage`/`setGuideCount`/`applyWheel`.
   *
   * Mounts backing *before* texture when enabled so the fill sits under
   * the wheel art (the backing layer's z-index is between the base photo
   * and the texture layer) — see `viewport/wheel-backing-layer.ts`.
   */
  renderAppliedWheel() {
    const canvas = this.previewCanvas;
    if (!canvas || !this.hasAppliedWheel || !canvas.getContentSize()) {
      this.removeRenderedWheelLayers();
      return;
    }
    if (this.wheelBackingEnabled) {
      displayWheelBackingInCanvas(canvas, this.placementStore, this.pixelSource, this.backingOptions);
    } else {
      const backingLayer = canvas.getLayer("wheel-backing");
      if (backingLayer) {
        canvas.removeLayer("wheel-backing");
        backingLayer.destroy?.();
      }
    }
    this.currentWheelLayer = displayWheelInCanvas(canvas, this.placementStore, this.wheelLibrary, this.filterOptions, this.pixelSource, this.mirrored);
  }
  removeRenderedWheelLayers() {
    this.currentWheelLayer = null;
    const canvas = this.previewCanvas;
    if (!canvas) return;
    for (const id of RENDER_LAYER_IDS) {
      const layer = canvas.getLayer(id);
      if (!layer) continue;
      canvas.removeLayer(id);
      layer.destroy?.();
    }
  }
  requirePreviewCanvas() {
    if (!this.previewCanvas) {
      throw new Error("WheelVisualizer.mount() must be called before this method.");
    }
    return this.previewCanvas;
  }
};

// catalog/catalog-fetch.ts
function fetchJsonFromNetwork(url) {
  return fetch(url).then((response) => {
    if (!response.ok) {
      throw new Error(`Catalog request failed: ${url} (${response.status})`);
    }
    return response.json();
  });
}

// catalog/vehicle-catalog-loader.ts
var INDEX_FILE = "index.json";
var MANIFEST_FILE = "manifest.json";
var VehicleCatalogLoader = class {
  constructor(baseUrl, fetchJson = fetchJsonFromNetwork) {
    this.baseUrl = baseUrl;
    this.fetchJson = fetchJson;
  }
  /**
   * Loads every vehicle listed in the library's index. A vehicle whose
   * manifest is missing or malformed is skipped (logged) rather than
   * failing the whole library; a missing/malformed *index* is fatal —
   * the same resilience split as `CatalogLoader.loadCatalog`.
   */
  async loadVehicles() {
    const ids = await this.loadIndex();
    const vehicles = await Promise.all(ids.map((id) => this.loadVehicle(id)));
    return vehicles.filter((vehicle) => vehicle !== null);
  }
  async loadIndex() {
    const indexUrl = this.resolve(INDEX_FILE);
    const raw = await this.fetchJson(indexUrl);
    if (!Array.isArray(raw) || !raw.every((id) => typeof id === "string")) {
      throw new Error(`Vehicle Library index at ${indexUrl} is not a JSON array of strings.`);
    }
    return raw;
  }
  async loadVehicle(id) {
    const manifestUrl = this.resolve(`${id}/${MANIFEST_FILE}`);
    try {
      const raw = await this.fetchJson(manifestUrl);
      return manifestToVehicleAsset(raw, this.resolve(id));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      console.warn(`Skipping library vehicle "${id}": ${message}`);
      return null;
    }
  }
  resolve(path) {
    return `${this.baseUrl.replace(/\/+$/, "")}/${path}`;
  }
};
function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function parseGuide(raw) {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("Guide is not an object.");
  }
  const guide = raw;
  if (!isFiniteNumber(guide.cx) || !isFiniteNumber(guide.cy) || !isFiniteNumber(guide.majorAxis) || !isFiniteNumber(guide.minorAxis) || !isFiniteNumber(guide.angle) || guide.majorAxis <= 0 || guide.minorAxis <= 0) {
    throw new Error("Guide must have finite cx/cy/angle and positive majorAxis/minorAxis.");
  }
  return { cx: guide.cx, cy: guide.cy, majorAxis: guide.majorAxis, minorAxis: guide.minorAxis, angle: guide.angle };
}
function manifestToVehicleAsset(raw, vehicleBaseUrl) {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("Manifest is not an object.");
  }
  const manifest = raw;
  if (typeof manifest.id !== "string" || manifest.id.length === 0) {
    throw new Error('Manifest is missing a valid "id".');
  }
  if (typeof manifest.brand !== "string" || manifest.brand.length === 0) {
    throw new Error(`Manifest "${manifest.id}" is missing a valid "brand".`);
  }
  if (typeof manifest.model !== "string" || manifest.model.length === 0) {
    throw new Error(`Manifest "${manifest.id}" is missing a valid "model".`);
  }
  if (typeof manifest.scenes !== "object" || manifest.scenes === null || Object.keys(manifest.scenes).length === 0) {
    throw new Error(`Manifest "${manifest.id}" has no scenes.`);
  }
  const base = vehicleBaseUrl.replace(/\/+$/, "");
  const scenes = {};
  for (const [viewId, scene] of Object.entries(manifest.scenes)) {
    const entry = scene;
    if (typeof entry !== "object" || entry === null || typeof entry.image !== "string" || entry.image.length === 0) {
      throw new Error(`Manifest "${manifest.id}" scene "${viewId}" is missing a valid "image".`);
    }
    if (!Array.isArray(entry.guides) || entry.guides.length === 0) {
      throw new Error(`Manifest "${manifest.id}" scene "${viewId}" has no guides.`);
    }
    scenes[viewId] = {
      imageUrl: `${base}/${entry.image}`,
      guides: entry.guides.map((guide) => {
        try {
          return parseGuide(guide);
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          throw new Error(`Manifest "${manifest.id}" scene "${viewId}": ${message}`);
        }
      })
    };
  }
  return { id: manifest.id, brand: manifest.brand, model: manifest.model, scenes };
}

// api/shopify-studio/shopify-catalog.ts
function isNonEmptyString(value) {
  return typeof value === "string" && value.length > 0;
}
function parseShopifyWheel(raw) {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw;
  if (!isNonEmptyString(entry.id) || !isNonEmptyString(entry.name) || !isNonEmptyString(entry.imageUrl)) return null;
  const isPreviewTexture = entry.isPreviewTexture === true;
  const thumbnailUrl = isNonEmptyString(entry.featuredImageUrl) ? entry.featuredImageUrl : entry.imageUrl;
  return {
    id: entry.id,
    name: entry.name,
    thumbnailUrl,
    finishes: [{ id: "default", name: "Default", textureUrl: entry.imageUrl, isTintFallback: false, isPreviewTexture }],
    shopify: {
      productId: entry.id,
      ...isNonEmptyString(entry.handle) ? { handle: entry.handle } : {},
      title: entry.name,
      ...isNonEmptyString(entry.productUrl) ? { url: entry.productUrl } : {},
      ...isNonEmptyString(entry.modelCode) ? { modelCode: entry.modelCode } : {},
      ...isNonEmptyString(entry.finishGroup) ? { finishGroup: entry.finishGroup } : {},
      ...isNonEmptyString(entry.productType) ? { productType: entry.productType } : {},
      ...isNonEmptyString(entry.tags) ? { tags: entry.tags } : {}
    }
  };
}
function parseShopifyCatalogSource(json) {
  const raw = JSON.parse(json);
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const wheel = parseShopifyWheel(entry);
    if (!wheel) {
      console.warn("DHA Wheel Studio: skipping malformed Shopify-hosted catalog entry", entry);
    } else if (wheel.finishes[0]?.isPreviewTexture) {
      console.warn(
        `DHA Wheel Studio: product "${wheel.name}" has no wheel.studio_texture metafield \u2014 showing its product photo as a preview-only placeholder texture.`
      );
    }
    return wheel;
  }).filter((wheel) => wheel !== null);
}
function readShopifyCatalogSource(root) {
  const tag = root.querySelector('script[type="application/json"][data-shopify-catalog]');
  if (!tag?.textContent) return [];
  try {
    return parseShopifyCatalogSource(tag.textContent);
  } catch (error) {
    console.error("DHA Wheel Studio: Shopify-hosted catalog JSON is invalid \u2014 the wheel library will be empty.", error);
    return [];
  }
}

// api/shopify-vehicles/wheel-guides.ts
var WHEEL_GUIDES_VERSION = 1;
function isFiniteNumber2(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function parseGeometry(raw, label) {
  if (typeof raw !== "object" || raw === null) {
    throw new Error(`${label} guide has no geometry object.`);
  }
  const geometry = raw;
  if (!isFiniteNumber2(geometry.cx) || !isFiniteNumber2(geometry.cy) || !isFiniteNumber2(geometry.majorAxis) || !isFiniteNumber2(geometry.minorAxis) || !isFiniteNumber2(geometry.angle) || geometry.majorAxis <= 0 || geometry.minorAxis <= 0) {
    throw new Error(`${label} guide needs finite cx/cy/angle and positive majorAxis/minorAxis.`);
  }
  return {
    cx: geometry.cx,
    cy: geometry.cy,
    majorAxis: geometry.majorAxis,
    minorAxis: geometry.minorAxis,
    angle: geometry.angle
  };
}
function parseWheelGuides(raw) {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      throw new Error("wheel_guides is not valid JSON.");
    }
  }
  if (typeof value !== "object" || value === null) {
    throw new Error("wheel_guides is not an object.");
  }
  const payload = value;
  if (payload.version !== WHEEL_GUIDES_VERSION) {
    throw new Error(`wheel_guides version ${String(payload.version)} is not supported (expected ${WHEEL_GUIDES_VERSION}).`);
  }
  if (!Array.isArray(payload.guides) || payload.guides.length !== 2) {
    throw new Error("wheel_guides must contain exactly two guides.");
  }
  const byPosition = /* @__PURE__ */ new Map();
  for (const entry of payload.guides) {
    const position = entry?.position;
    if (position !== "front" && position !== "rear") {
      throw new Error('Each wheel guide needs a "front" or "rear" position.');
    }
    if (byPosition.has(position)) {
      throw new Error(`wheel_guides has two "${position}" guides.`);
    }
    byPosition.set(position, parseGeometry(entry.geometry, position));
  }
  const front = byPosition.get("front");
  const rear = byPosition.get("rear");
  if (!front || !rear) {
    throw new Error("wheel_guides needs one front and one rear guide.");
  }
  return {
    version: WHEEL_GUIDES_VERSION,
    guides: [
      { position: "front", geometry: front },
      { position: "rear", geometry: rear }
    ]
  };
}
function wheelGuideGeometries(payload) {
  return payload.guides.map((guide) => guide.geometry);
}

// api/shopify-showroom/shopify-vehicles.ts
function isNonEmptyString2(value) {
  return typeof value === "string" && value.length > 0;
}
function parseShopifyVehicle(raw) {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw;
  const label = `${String(entry.brand ?? "?")} ${String(entry.model ?? "?")}`;
  if (!isNonEmptyString2(entry.id) || !isNonEmptyString2(entry.brand) || !isNonEmptyString2(entry.model)) {
    console.warn("DHA Vehicle Showroom: skipping a Shopify vehicle with missing id/brand/model.", entry);
    return null;
  }
  if (!isNonEmptyString2(entry.imageUrl)) {
    console.warn(`DHA Vehicle Showroom: skipping "${label}" \u2014 no image uploaded in Shopify Admin.`);
    return null;
  }
  let guides;
  try {
    guides = wheelGuideGeometries(parseWheelGuides(entry.wheelGuides));
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown error";
    console.warn(`DHA Vehicle Showroom: skipping "${label}" \u2014 no usable wheel positions (author them in the Admin Tool): ${message}`);
    return null;
  }
  const originalWidth = typeof entry.originalWidth === "number" && Number.isFinite(entry.originalWidth) && entry.originalWidth > 0 ? entry.originalWidth : void 0;
  const sortOrder = typeof entry.sortOrder === "number" && Number.isFinite(entry.sortOrder) ? entry.sortOrder : null;
  return {
    sortOrder,
    vehicle: {
      id: entry.id,
      brand: entry.brand,
      model: entry.model,
      thumbnailUrl: isNonEmptyString2(entry.thumbnailUrl) ? entry.thumbnailUrl : void 0,
      scenes: {
        side: {
          imageUrl: entry.imageUrl,
          guides,
          ...originalWidth !== void 0 ? { authoredImageWidth: originalWidth } : {}
        }
      }
    }
  };
}
function parseShopifyVehiclesSource(json) {
  let raw;
  try {
    raw = JSON.parse(json);
  } catch (error) {
    console.error("DHA Vehicle Showroom: Shopify vehicles JSON is invalid \u2014 no vehicles will be shown.", error);
    return [];
  }
  if (!Array.isArray(raw)) return [];
  return raw.map(parseShopifyVehicle).filter((entry) => entry !== null).sort((a, b) => {
    const aOrder = a.sortOrder ?? Number.POSITIVE_INFINITY;
    const bOrder = b.sortOrder ?? Number.POSITIVE_INFINITY;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return `${a.vehicle.brand} ${a.vehicle.model}`.localeCompare(`${b.vehicle.brand} ${b.vehicle.model}`);
  }).map((entry) => entry.vehicle);
}
function readShopifyVehiclesSource(root) {
  const tag = root.querySelector('script[type="application/json"][data-shopify-vehicles]');
  if (!tag?.textContent) return [];
  return parseShopifyVehiclesSource(tag.textContent);
}

// api/shopify-showroom/showroom-scenes.ts
var KNOWN_VIEW_IDS = /* @__PURE__ */ new Set(["side"]);
function toShowroomVehicles(assets) {
  return assets.map((asset) => {
    const scenes = {};
    for (const [viewId, scene] of Object.entries(asset.scenes)) {
      if (KNOWN_VIEW_IDS.has(viewId)) scenes[viewId] = scene;
    }
    return { id: asset.id, brand: asset.brand, model: asset.model, scenes };
  }).filter((vehicle) => Object.keys(vehicle.scenes).length > 0);
}

// api/shopify-showroom/showroom-scene-sync.ts
function showroomSceneKey(selection) {
  return selection.vehicleId ? `${selection.vehicleId}/${selection.viewId}` : null;
}
function shouldSyncShowroomScene(selection, activeSceneKey, pendingSceneKey) {
  const sceneKey = showroomSceneKey(selection);
  return sceneKey !== null && sceneKey !== activeSceneKey && sceneKey !== pendingSceneKey;
}

// api/shopify-showroom/showroom-state.ts
var ShowroomState = class {
  selection;
  listeners = /* @__PURE__ */ new Set();
  constructor(initial = {}) {
    this.selection = {
      vehicleId: null,
      viewId: "side",
      wheelId: null,
      finishId: null,
      ...initial
    };
  }
  get() {
    return { ...this.selection };
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  /** Switching vehicles keeps the chosen wheel/finish — carrying "your wheel" across cars is the showroom's core browsing loop. */
  selectVehicle(vehicleId) {
    if (vehicleId === this.selection.vehicleId) return;
    this.update({ vehicleId });
  }
  selectView(viewId) {
    if (viewId === this.selection.viewId) return;
    this.update({ viewId });
  }
  /** Switching wheels resets the finish to the new wheel's default — finishes belong to a wheel. Re-selecting the same wheel keeps its finish. */
  selectWheel(wheelId) {
    if (wheelId === this.selection.wheelId) return;
    this.update({ wheelId, finishId: null });
  }
  selectFinish(finishId) {
    if (finishId === this.selection.finishId) return;
    this.update({ finishId });
  }
  update(patch) {
    this.selection = { ...this.selection, ...patch };
    const snapshot = this.get();
    for (const listener of this.listeners) listener(snapshot);
  }
};

// api/shopify-showroom/showroom-ui.ts
function requireElement(root, selector) {
  const element = root.querySelector(selector);
  if (!element) throw new Error(`DHA Vehicle Showroom markup is missing ${selector}`);
  return element;
}
function deriveWheelHeading(wheel, fallbackSeries) {
  if (!wheel) return { series: fallbackSeries, model: "" };
  const parts = wheel.name.split("|").map((part) => part.trim()).filter((part) => part.length > 0);
  const modelCode = wheel.modelCode?.trim() || "";
  if (parts.length > 1) {
    return { series: parts.slice(0, -1).join(" \xB7 "), model: modelCode || parts[parts.length - 1] };
  }
  if (modelCode && modelCode !== wheel.name.trim()) {
    return { series: wheel.name.trim(), model: modelCode };
  }
  return { series: fallbackSeries, model: wheel.name.trim() };
}
function wheelMatchesFilter(wheel, query) {
  const tokens = query.toLowerCase().replace(/[-_]/g, " ").split(/\s+/).filter((token) => token.length > 0);
  if (tokens.length === 0) return true;
  const haystack = [
    wheel.name,
    wheel.shopify?.modelCode,
    wheel.shopify?.finishGroup,
    wheel.shopify?.handle,
    wheel.shopify?.productType,
    wheel.shopify?.tags
  ].filter((part) => typeof part === "string" && part.length > 0).join(" ").toLowerCase().replace(/[-_]/g, " ");
  return tokens.every((token) => haystack.includes(token));
}
var ShowroomUi = class _ShowroomUi {
  root;
  /** PreviewCanvas host — showroom-main mounts the engine into this. */
  previewHost;
  handlers;
  vehicles;
  vehiclesEmpty;
  library;
  libraryEmpty;
  wheelFilter;
  filterEmpty;
  /** The last renderLibrary() arguments — re-rendered through the filter as the customer types, without the host resupplying them. */
  lastLibraryWheels = [];
  lastAppliedWheelId = null;
  wheelSeries;
  wheelModel;
  /** The Liquid-rendered series label, kept as the heading fallback until a wheel drives it. */
  fallbackSeriesText;
  finishes;
  flipToggle;
  quoteSummary;
  status;
  actionButtons;
  // Look Closer modal — a larger viewing mode of the same session. The
  // live preview node is MOVED into the modal stage while open (same
  // engine, same state, no second renderer) and moved back on close.
  previewWrap;
  modal;
  modalStage;
  modalLibrary;
  modalClose;
  lookCloserButton;
  modalKeydownHandler = null;
  previousBodyOverflow = "";
  constructor(root, handlers) {
    this.root = root;
    this.handlers = handlers;
    this.previewHost = requireElement(root, "[data-showroom-preview]");
    this.vehicles = requireElement(root, "[data-showroom-vehicles]");
    this.vehiclesEmpty = root.querySelector("[data-showroom-vehicles-empty]");
    this.library = requireElement(root, "[data-showroom-library]");
    this.libraryEmpty = root.querySelector("[data-showroom-library-empty]");
    this.wheelFilter = root.querySelector("[data-showroom-wheel-filter]");
    this.filterEmpty = root.querySelector("[data-showroom-filter-empty]");
    this.wheelFilter?.addEventListener("input", () => this.renderFilteredLibrary());
    this.wheelSeries = root.querySelector("[data-showroom-wheel-series]");
    this.wheelModel = root.querySelector("[data-showroom-wheel-model]");
    this.fallbackSeriesText = this.wheelSeries?.textContent?.trim() ?? "";
    this.finishes = requireElement(root, "[data-showroom-finishes]");
    this.quoteSummary = requireElement(root, "[data-showroom-summary]");
    this.status = requireElement(root, "[data-showroom-status]");
    this.actionButtons = Array.from(root.querySelectorAll("[data-showroom-action]"));
    this.flipToggle = this.actionButtons.find((button) => button.dataset.showroomAction === "flip-wheels") ?? null;
    this.previewWrap = root.querySelector("[data-showroom-preview-wrap]");
    this.modal = root.querySelector("[data-showroom-modal]");
    this.modalStage = root.querySelector("[data-showroom-modal-stage]");
    this.modalLibrary = root.querySelector("[data-showroom-modal-library]");
    this.modalClose = root.querySelector("[data-showroom-modal-close]");
    this.lookCloserButton = this.actionButtons.find((button) => button.dataset.showroomAction === "look-closer") ?? null;
    for (const button of this.actionButtons) {
      button.addEventListener("click", () => {
        const action = button.dataset.showroomAction;
        if (action) this.handlers.onAction(action);
      });
    }
    this.previewWrap?.addEventListener("click", () => this.handlers.onAction("look-closer"));
    this.modalClose?.addEventListener("click", () => this.closeLookCloser());
    this.modal?.querySelector("[data-showroom-modal-backdrop]")?.addEventListener("click", () => this.closeLookCloser());
  }
  /** Applies a UI state. Which stage is visible is pure CSS keyed on the root's `data-state`; this only maintains the attribute. */
  setState(state) {
    this.root.dataset.state = state;
  }
  setActionEnabled(action, enabled) {
    for (const button of this.actionButtons) {
      if (button.dataset.showroomAction === action) button.disabled = !enabled;
    }
  }
  setStatus(message, isError) {
    this.status.textContent = message;
    this.status.classList.toggle("is-error", isError);
    this.status.hidden = message.length === 0;
  }
  /** Shows/hides the "no vehicles yet" message — the Vehicle Library was unreachable, empty, or no server URL is configured. */
  setVehiclesEmpty(isEmpty) {
    if (this.vehiclesEmpty) this.vehiclesEmpty.hidden = !isEmpty;
  }
  // --- Look Closer modal ---------------------------------------------------
  isLookCloserOpen() {
    return this.modal !== null && !this.modal.hidden;
  }
  /**
   * Opens the Look Closer modal: moves the live preview node into the
   * modal stage (same engine and state — closing puts it back), locks
   * background scrolling, traps focus, and fades in. No-ops if the
   * modal markup is absent or already open.
   */
  openLookCloser() {
    if (!this.modal || !this.modalStage || this.isLookCloserOpen()) return;
    this.modalStage.appendChild(this.previewHost);
    this.modal.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => this.modal?.classList.add("is-open")));
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.modalKeydownHandler = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeLookCloser();
        return;
      }
      if (event.key === "Tab") this.trapModalFocus(event);
    };
    document.addEventListener("keydown", this.modalKeydownHandler, true);
    this.handlers.onLookCloserToggled(true);
    this.modalClose?.focus();
  }
  /** Closes the modal, returns the preview to its place on the page, and restores focus to the Look Closer button. */
  closeLookCloser() {
    if (!this.modal || !this.isLookCloserOpen()) return;
    this.previewWrap?.insertBefore(this.previewHost, this.previewWrap.firstChild);
    if (this.modalKeydownHandler) {
      document.removeEventListener("keydown", this.modalKeydownHandler, true);
      this.modalKeydownHandler = null;
    }
    document.body.style.overflow = this.previousBodyOverflow;
    const modal = this.modal;
    modal.classList.remove("is-open");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      modal.hidden = true;
    } else {
      window.setTimeout(() => {
        modal.hidden = true;
      }, 200);
    }
    this.handlers.onLookCloserToggled(false);
    this.lookCloserButton?.focus();
  }
  /** Keeps Tab/Shift+Tab cycling inside the open modal. */
  trapModalFocus(event) {
    if (!this.modal) return;
    const focusable = Array.from(this.modal.querySelectorAll("button:not([disabled])"));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !this.modal.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !this.modal.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }
  /** Renders the vehicle selector cards — thumbnail (when the source provides one) over brand/model. A flat curated gallery, deliberately not a brand/model/year wizard (V1 scope). */
  renderVehicles(vehicles, selectedId) {
    this.vehicles.innerHTML = "";
    for (const vehicle of vehicles) {
      const card = document.createElement("button");
      card.type = "button";
      card.className = "dha-showroom__vehicle-card";
      card.classList.toggle("is-selected", vehicle.id === selectedId);
      card.setAttribute("aria-pressed", String(vehicle.id === selectedId));
      card.addEventListener("click", () => this.handlers.onSelectVehicle(vehicle.id));
      if (vehicle.thumbnailUrl) {
        const thumb = document.createElement("span");
        thumb.className = "dha-showroom__vehicle-thumb";
        const img = document.createElement("img");
        img.src = vehicle.thumbnailUrl;
        img.alt = "";
        img.loading = "lazy";
        img.draggable = false;
        thumb.appendChild(img);
        card.appendChild(thumb);
      }
      const meta = document.createElement("span");
      meta.className = "dha-showroom__vehicle-meta";
      const brand = document.createElement("span");
      brand.className = "dha-showroom__vehicle-brand";
      brand.textContent = vehicle.brand;
      meta.appendChild(brand);
      const model = document.createElement("span");
      model.className = "dha-showroom__vehicle-model";
      model.textContent = vehicle.model;
      meta.appendChild(model);
      card.appendChild(meta);
      this.vehicles.appendChild(card);
    }
  }
  /** The wheel picker's per-card second line — same convention as the Studio (default finish, "+N" for the rest). */
  static finishSummary(wheel) {
    const defaultFinish = wheel.finishes[0];
    if (!defaultFinish) return "";
    return wheel.finishes.length > 1 ? `${defaultFinish.name} +${wheel.finishes.length - 1}` : defaultFinish.name;
  }
  /** Builds one wheel card button — used by both the panel grid and the modal strip, so the two stay a single behavior. */
  buildWheelCard(wheel, appliedWheelId) {
    const card = document.createElement("button");
    card.type = "button";
    card.className = "dha-showroom__wheel-card";
    card.classList.toggle("is-selected", wheel.id === appliedWheelId);
    card.setAttribute("aria-pressed", String(wheel.id === appliedWheelId));
    card.setAttribute("aria-label", `Select ${wheel.name}`);
    card.dataset.wheelId = wheel.id;
    card.title = wheel.name;
    card.addEventListener("click", () => this.handlers.onSelectWheel(wheel.id));
    const thumb = document.createElement("span");
    thumb.className = "dha-showroom__wheel-thumb";
    const img = document.createElement("img");
    img.src = wheel.thumbnailUrl;
    img.alt = wheel.name;
    img.loading = "lazy";
    img.draggable = false;
    thumb.appendChild(img);
    if (wheel.finishes[0]?.isPreviewTexture) {
      const badge = document.createElement("span");
      badge.className = "dha-showroom__wheel-badge";
      badge.textContent = "Preview";
      badge.title = "No Studio texture set for this product yet \u2014 showing its product photo instead.";
      thumb.appendChild(badge);
    }
    card.appendChild(thumb);
    const name = document.createElement("span");
    name.className = "dha-showroom__wheel-name";
    name.textContent = deriveWheelHeading({ name: wheel.name, modelCode: wheel.shopify?.modelCode }, "").model;
    card.appendChild(name);
    const finishSummary = _ShowroomUi.finishSummary(wheel);
    const finishes = document.createElement("span");
    finishes.className = "dha-showroom__wheel-finishes";
    finishes.textContent = finishSummary;
    card.appendChild(finishes);
    return card;
  }
  /** Renders the wheel cards into the panel grid AND the modal strip (same wheels, same handler, shared state). `appliedWheelId` is null until the customer applies a wheel. */
  renderLibrary(wheels, appliedWheelId) {
    this.lastLibraryWheels = wheels;
    this.lastAppliedWheelId = appliedWheelId;
    this.renderFilteredLibrary();
  }
  /** The actual card render — `renderLibrary`'s cached list narrowed by the filter input. Runs again on every filter keystroke. */
  renderFilteredLibrary() {
    const focused = document.activeElement;
    const focusedWheelId = focused?.closest("[data-wheel-id]")?.dataset.wheelId ?? null;
    const focusedInModal = focusedWheelId !== null && this.modalLibrary !== null && this.modalLibrary.contains(focused);
    const containers = [this.library, ...this.modalLibrary ? [this.modalLibrary] : []];
    for (const container of containers) container.innerHTML = "";
    const query = this.wheelFilter?.value ?? "";
    const wheels = this.lastLibraryWheels.filter((wheel) => wheelMatchesFilter(wheel, query));
    const catalogEmpty = this.lastLibraryWheels.length === 0;
    this.library.hidden = wheels.length === 0;
    if (this.libraryEmpty) this.libraryEmpty.hidden = !catalogEmpty;
    if (this.filterEmpty) this.filterEmpty.hidden = catalogEmpty || wheels.length > 0;
    if (this.wheelFilter) this.wheelFilter.hidden = catalogEmpty;
    if (wheels.length === 0) return;
    for (const container of containers) {
      for (const wheel of wheels) {
        container.appendChild(this.buildWheelCard(wheel, this.lastAppliedWheelId));
      }
    }
    if (focusedWheelId) {
      const home = focusedInModal ? this.modalLibrary : this.library;
      home?.querySelector(`[data-wheel-id="${CSS.escape(focusedWheelId)}"]`)?.focus();
    }
  }
  /** Writes the left panel's selected-wheel heading (series line + model line) — see `deriveWheelHeading`. */
  renderWheelHeading(wheel) {
    if (!this.wheelSeries && !this.wheelModel) return;
    const { series, model } = deriveWheelHeading(
      wheel ? { name: wheel.name, ...wheel.shopify?.modelCode ? { modelCode: wheel.shopify.modelCode } : {} } : null,
      this.fallbackSeriesText
    );
    if (this.wheelSeries) {
      this.wheelSeries.textContent = series;
      this.wheelSeries.hidden = series.length === 0;
    }
    if (this.wheelModel) {
      this.wheelModel.textContent = model;
      this.wheelModel.hidden = model.length === 0;
    }
  }
  /** Renders the finish chips for the applied wheel (hidden entirely for single-finish wheels). */
  renderFinishes(wheel, activeFinishId) {
    this.finishes.innerHTML = "";
    const show = wheel !== null && wheel.finishes.length > 1;
    this.finishes.hidden = !show;
    if (!show || !wheel) return;
    for (const finish of wheel.finishes) {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "dha-showroom__finish-chip";
      chip.classList.toggle("is-active", finish.id === activeFinishId);
      chip.setAttribute("aria-pressed", String(finish.id === activeFinishId));
      chip.textContent = finish.name;
      chip.addEventListener("click", () => this.handlers.onSelectFinish(wheel.id, finish.id));
      this.finishes.appendChild(chip);
    }
  }
  /** Reflects the Flip Wheels toggle's pressed state (`aria-pressed` + a matching CSS hook). No-ops if the section's markup has no flip control. */
  setWheelMirrored(mirrored) {
    if (!this.flipToggle) return;
    this.flipToggle.setAttribute("aria-pressed", String(mirrored));
  }
  /** Fills the quote stage: the visible summary card and the contact form's hidden fields. */
  renderQuote(vehicle, wheel, finishName) {
    this.quoteSummary.innerHTML = "";
    if (wheel) {
      const title = document.createElement("p");
      title.className = "dha-showroom__summary-title";
      title.textContent = finishName ? `${wheel.name} \u2014 ${finishName}` : wheel.name;
      this.quoteSummary.appendChild(title);
    }
    if (vehicle) {
      const context = document.createElement("p");
      context.className = "dha-showroom__summary-context";
      context.textContent = `Previewed on: ${vehicle.brand} ${vehicle.model}`;
      this.quoteSummary.appendChild(context);
    }
    this.setQuoteField("wheel", wheel ? wheel.name : "");
    this.setQuoteField("wheelProductId", wheel?.shopify?.productId ?? "");
    this.setQuoteField("wheelProductHandle", wheel?.shopify?.handle ?? "");
    this.setQuoteField("wheelProductUrl", wheel?.shopify?.url ?? "");
    this.setQuoteField("finish", finishName ?? "");
    this.setQuoteField("vehicle", vehicle ? `${vehicle.brand} ${vehicle.model}` : "");
  }
  setQuoteField(field, value) {
    const input = this.root.querySelector(`[data-showroom-field="${field}"]`);
    if (input) input.value = value;
  }
};

// api/shopify-showroom/showroom-main.ts
var SCENE_ERROR_COPY = "The showroom scene didn\u2019t load \u2014 please refresh to try again.";
var INSPECT_ZOOM = {
  /** Opening zoom on fine-pointer (desktop) devices. Touch devices always open at the fitted view. */
  defaultLevel: 1.5,
  /** Floor — the fitted view. The pan constraint centers a fitted image, so dragging is a no-op here. */
  minLevel: 1,
  /** Default ceiling — 600%. Can be overridden by the Shopify section setting. */
  maxLevel: 6,
  /** Per-click factor for the toolbar's +/− buttons. */
  buttonStep: 1.4,
  /** Length of the eased zoom used by toolbar/double-click zooms (skipped under prefers-reduced-motion). */
  animationMs: 180
};
function parseMaxZoomLevel(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= INSPECT_ZOOM.minLevel) return INSPECT_ZOOM.maxLevel;
  return parsed;
}
var VehicleShowroom = class {
  ui;
  config;
  state;
  vehicles = [];
  wheels = [];
  /**
   * The shared engine — the same one the upload Studio and Admin Tool
   * mount. `guideEditingEnabled: false` is the entire difference in how
   * it's driven: guides arrive via `loadScene` and are never shown or
   * editable, so no ring theme is needed.
   */
  engine = new WheelVisualizer({
    guideEditingEnabled: false,
    viewportInteractionEnabled: false,
    wheelBackingEnabled: false
  });
  /** Guards against a slow scene load landing after the customer has already switched to another vehicle/view. */
  sceneToken = 0;
  /** Flip Wheels toggle state — a customer-facing choice, deliberately independent of wheel/vehicle selection (see `handleAction`'s 'flip-wheels' case). */
  mirrored = false;
  activeSceneKey = null;
  pendingSceneKey = null;
  /** The in-flight eased-zoom animation frame, if any — see animateZoomTo. */
  zoomAnimationFrame = 0;
  constructor(root) {
    this.config = {
      vehicleSource: root.dataset.vehicleSource === "asset_library" ? "asset_library" : "shopify",
      assetLibraryUrl: root.dataset.assetLibraryUrl ?? "",
      maxZoomLevel: parseMaxZoomLevel(root.dataset.maxZoomLevel)
    };
    this.state = new ShowroomState();
    this.wheels = readShopifyCatalogSource(root);
    this.engine.wheelLibrary.setWheels(this.wheels);
    void this.engine.wheelLibrary.preloadAll();
    this.ui = new ShowroomUi(root, {
      onSelectVehicle: (vehicleId) => this.state.selectVehicle(vehicleId),
      onSelectWheel: (wheelId) => this.applyWheel(wheelId),
      onSelectFinish: (wheelId, finishId) => {
        this.state.selectFinish(finishId);
        this.engine.wheelLibrary.selectFinish(wheelId, finishId);
      },
      onAction: (action) => this.handleAction(action),
      onLookCloserToggled: (open) => this.handleLookCloserToggled(open)
    });
    this.engine.mount(this.ui.previewHost);
    const canvas = this.engine.getPreviewCanvas();
    canvas.setRelativeScaleBounds({ min: INSPECT_ZOOM.minLevel, max: this.config.maxZoomLevel });
    canvas.setConstrainToContainer(true);
    canvas.onDoubleActivate((point) => this.toggleInspectZoom(point));
    canvas.onViewportChange(() => this.updateZoomChrome());
    this.engine.wheelLibrary.subscribe(() => this.renderCatalogChrome());
    this.state.subscribe((selection) => this.handleSelectionChanged(selection));
    this.ui.setState("browse");
    const initialWheelId = pickInitialWheelId(this.wheels, globalThis.location?.search ?? "");
    if (initialWheelId) this.state.selectWheel(initialWheelId);
    this.renderVehicleChrome();
    this.renderCatalogChrome();
    if (this.config.vehicleSource === "shopify") {
      this.applyLoadedVehicles(readShopifyVehiclesSource(root));
    } else if (this.config.assetLibraryUrl) {
      void this.loadVehicleLibrary();
    } else {
      this.ui.setVehiclesEmpty(true);
    }
  }
  destroy() {
    cancelAnimationFrame(this.zoomAnimationFrame);
    this.engine.destroy();
  }
  /** Applies a loaded vehicle list (either source): empty state, chrome, and auto-selecting the first vehicle. */
  applyLoadedVehicles(vehicles) {
    this.vehicles = vehicles;
    this.ui.setVehiclesEmpty(vehicles.length === 0);
    const first = vehicles[0];
    if (first) {
      this.state.selectVehicle(first.id);
    } else {
      this.renderVehicleChrome();
    }
  }
  /**
   * Fetches the Vehicle Library and puts the first vehicle on stage.
   * Fail-soft like the wheel catalog: an unreachable or empty library
   * just shows the vehicles empty state — the wheel rail (and the rest
   * of the page) is unaffected.
   */
  async loadVehicleLibrary() {
    const vehicleLibraryBaseUrl = `${this.config.assetLibraryUrl.replace(/\/+$/, "")}/vehicles`;
    let vehicles = [];
    try {
      vehicles = toShowroomVehicles(await new VehicleCatalogLoader(vehicleLibraryBaseUrl).loadVehicles());
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      console.warn(`DHA Vehicle Showroom: Vehicle Library unavailable (${vehicleLibraryBaseUrl}): ${message}`);
    }
    this.applyLoadedVehicles(vehicles);
  }
  // --- selection → engine ----------------------------------------------
  currentVehicle() {
    const { vehicleId } = this.state.get();
    return this.vehicles.find((vehicle) => vehicle.id === vehicleId) ?? null;
  }
  handleSelectionChanged(selection) {
    this.renderVehicleChrome();
    if (!shouldSyncShowroomScene(selection, this.activeSceneKey, this.pendingSceneKey)) return;
    const sceneKey = showroomSceneKey(selection);
    if (!sceneKey) return;
    this.pendingSceneKey = sceneKey;
    void this.syncScene(sceneKey);
  }
  /**
   * Loads the current vehicle+view scene into the engine, then re-applies
   * the customer's wheel (loading a new image clears applied-wheel
   * status — see `WheelVisualizer.loadScene`). A fresh `LoadedImage` is
   * fetched per switch on purpose: `displayImageInCanvas` revokes the
   * previous image's object URL, so a cached `LoadedImage` must never be
   * re-displayed; the browser's HTTP cache makes the refetch cheap.
   */
  async syncScene(sceneKey) {
    const { viewId } = this.state.get();
    const scene = this.currentVehicle()?.scenes[viewId];
    if (!scene) return;
    const token = ++this.sceneToken;
    try {
      const image = await loadImageFromUrl(scene.imageUrl);
      if (token !== this.sceneToken) return;
      this.ui.setStatus("", false);
      const scale = scene.authoredImageWidth ? image.dimensions.width / scene.authoredImageWidth : 1;
      const guides = scale === 1 ? scene.guides : scene.guides.map((guide) => ({
        cx: guide.cx * scale,
        cy: guide.cy * scale,
        majorAxis: guide.majorAxis * scale,
        minorAxis: guide.minorAxis * scale,
        angle: guide.angle
      }));
      this.engine.loadScene(image, { imageUrl: scene.imageUrl, guides });
      this.activeSceneKey = sceneKey;
      if (this.pendingSceneKey === sceneKey) this.pendingSceneKey = null;
      const { wheelId } = this.state.get();
      if (wheelId) this.engine.applyWheel(wheelId);
      this.renderCatalogChrome();
    } catch (error) {
      if (token !== this.sceneToken) return;
      if (this.pendingSceneKey === sceneKey) this.pendingSceneKey = null;
      const message = error instanceof Error ? error.message : "Unknown error";
      console.warn(`DHA Vehicle Showroom: scene failed to load: ${message}`);
      this.ui.setStatus(SCENE_ERROR_COPY, true);
    }
  }
  applyWheel(wheelId) {
    this.state.selectWheel(wheelId);
    this.engine.applyWheel(wheelId);
    this.renderCatalogChrome();
  }
  // --- chrome ------------------------------------------------------------
  handleAction(action) {
    switch (action) {
      case "request-quote":
        if (!this.engine.hasWheelApplied()) return;
        this.ui.renderQuote(this.currentVehicle(), this.appliedWheel(), this.appliedFinishName());
        this.ui.setState("quote");
        break;
      case "back-to-showroom":
        this.ui.setState("browse");
        break;
      case "look-closer":
        this.ui.openLookCloser();
        break;
      case "flip-wheels":
        this.mirrored = !this.mirrored;
        this.engine.setWheelMirrored(this.mirrored);
        this.ui.setWheelMirrored(this.mirrored);
        break;
      case "zoom-in":
      case "zoom-out": {
        const canvas = this.engine.getPreviewCanvas();
        const factor = action === "zoom-in" ? INSPECT_ZOOM.buttonStep : 1 / INSPECT_ZOOM.buttonStep;
        this.animateZoomTo(canvas.getTransform().scale * factor);
        break;
      }
      case "zoom-actual":
        this.animateZoomTo(1);
        break;
      case "zoom-fit": {
        const fitScale = this.engine.getPreviewCanvas().getFitScale();
        if (fitScale !== null) this.animateZoomTo(fitScale);
        break;
      }
      default:
        break;
    }
  }
  // --- Look Closer inspection zoom ---------------------------------------
  /**
   * The modal just opened or closed (the live preview node has already
   * been moved, so the canvas must remeasure either way). Open: turn the
   * engine's pan/zoom gestures on and, on fine-pointer devices, ease in
   * to the default inspection zoom — touch devices open at the fitted
   * view, like opening a photo in a phone's gallery. Close: gestures off
   * and back to the fitted, centered in-page preview.
   */
  handleLookCloserToggled(open) {
    const canvas = this.engine.getPreviewCanvas();
    canvas.remeasureContainer();
    if (open) {
      canvas.setInteractionEnabled(true);
      const fitScale = canvas.getFitScale();
      const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      if (fitScale !== null && finePointer && INSPECT_ZOOM.defaultLevel > 1) {
        this.animateZoomTo(fitScale * INSPECT_ZOOM.defaultLevel);
      }
    } else {
      cancelAnimationFrame(this.zoomAnimationFrame);
      canvas.setInteractionEnabled(false);
      canvas.fitToContainer();
    }
    this.updateZoomChrome();
  }
  /** Double-click/double-tap: zoom back to fit when inspecting, otherwise in to the default level around the tapped point. */
  toggleInspectZoom(point) {
    const canvas = this.engine.getPreviewCanvas();
    const fitScale = canvas.getFitScale();
    if (fitScale === null) return;
    const zoomedIn = canvas.getTransform().scale > fitScale * 1.02;
    if (zoomedIn) {
      this.animateZoomTo(fitScale);
    } else {
      this.animateZoomTo(fitScale * INSPECT_ZOOM.defaultLevel, point);
    }
  }
  /**
   * Eases the zoom to `targetScale` (around `focalPoint`, default stage
   * center) over a short curve so toolbar and double-click zooms feel
   * placed rather than teleported. Interpolates exponentially — equal
   * time covers equal zoom *ratio* — which is how optical zoom reads as
   * constant-speed. Jumps straight to the target under
   * prefers-reduced-motion.
   */
  animateZoomTo(targetScale, focalPoint) {
    const canvas = this.engine.getPreviewCanvas();
    cancelAnimationFrame(this.zoomAnimationFrame);
    const startScale = canvas.getTransform().scale;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion || startScale <= 0 || startScale === targetScale) {
      canvas.zoomTo(targetScale, focalPoint);
      return;
    }
    const startTime = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - startTime) / INSPECT_ZOOM.animationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      canvas.zoomTo(startScale * Math.pow(targetScale / startScale, eased), focalPoint);
      if (progress < 1) this.zoomAnimationFrame = requestAnimationFrame(step);
    };
    this.zoomAnimationFrame = requestAnimationFrame(step);
  }
  /** Keeps the modal toolbar's +/− buttons disabled at the zoom bounds. */
  updateZoomChrome() {
    const canvas = this.engine.getPreviewCanvas();
    const fitScale = canvas.getFitScale();
    if (fitScale === null || fitScale <= 0) return;
    const level = canvas.getTransform().scale / fitScale;
    this.ui.setActionEnabled("zoom-out", level > INSPECT_ZOOM.minLevel * 1.001);
    this.ui.setActionEnabled("zoom-in", level < this.config.maxZoomLevel * 0.999);
  }
  appliedWheel() {
    if (!this.engine.hasWheelApplied()) return null;
    const id = this.engine.wheelLibrary.getSelectedWheelId();
    return this.wheels.find((wheel) => wheel.id === id) ?? null;
  }
  appliedFinishName() {
    const wheel = this.appliedWheel();
    const finishId = this.engine.wheelLibrary.getSelectedFinishId();
    return wheel?.finishes.find((finish) => finish.id === finishId)?.name ?? null;
  }
  renderVehicleChrome() {
    const { vehicleId } = this.state.get();
    this.ui.renderVehicles(this.vehicles, vehicleId);
  }
  renderCatalogChrome() {
    const wheel = this.appliedWheel();
    const { wheelId } = this.state.get();
    const selectedWheelId = wheel?.id ?? wheelId;
    const selectedWheel = this.wheels.find((entry) => entry.id === selectedWheelId) ?? null;
    this.ui.renderLibrary(this.wheels, selectedWheelId);
    this.ui.renderWheelHeading(selectedWheel);
    this.ui.renderFinishes(wheel, this.engine.wheelLibrary.getSelectedFinishId());
    this.ui.setActionEnabled("request-quote", this.engine.hasWheelApplied());
  }
};
var showroomInstances = /* @__PURE__ */ new WeakMap();
function mountShowroom(root) {
  if (showroomInstances.has(root)) return;
  try {
    showroomInstances.set(root, new VehicleShowroom(root));
  } catch (error) {
    console.error("DHA Vehicle Showroom failed to start:", error);
  }
}
function mountShowrooms(scope = document) {
  for (const root of Array.from(scope.querySelectorAll("[data-dha-wheel-showroom]"))) {
    mountShowroom(root);
  }
}
mountShowrooms();
document.addEventListener("shopify:section:load", (event) => {
  mountShowrooms(event.target);
});
document.addEventListener("shopify:section:unload", (event) => {
  const root = event.target instanceof HTMLElement && event.target.matches("[data-dha-wheel-showroom]") ? event.target : event.target instanceof HTMLElement ? event.target.querySelector("[data-dha-wheel-showroom]") : null;
  if (!root) return;
  showroomInstances.get(root)?.destroy();
  showroomInstances.delete(root);
});
